import React, { useEffect, useState, useRef } from "react";
import axios from "axios";
import PublicationEditor from "./components/Editor/PublicationEditor";
import FooterDescriptionEditor from "./components/Editor/FooterDescriptionEditor";
import ImageCropModal from "./components/ImageCropModal";

import sanitizeRichHtml from
  "./utils/sanitizeRichHtml";

const MAX_IMAGE_SIZE =
  3 * 1024 * 1024;

const createPhotoItemId = () => {

  if (
    globalThis.crypto &&
    typeof globalThis.crypto.randomUUID ===
      "function"
  ) {
    return globalThis.crypto.randomUUID();
  }

  return (
    `photo-${Date.now()}-` +
    Math.random()
      .toString(36)
      .slice(2)
  );

};


const getPhotoFileSignature = (
  file
) => {

  return [
    file.name,
    file.size,
    file.lastModified
  ].join("::");

};

import { CONTENT_TAXONOMY } from "./config/contentTaxonomy";

import {
  getMediaUrl
} from "./config/api";

const getTodayDate = () => {
    const today = new Date();

    const day = String(today.getDate()).padStart(2, "0");
    const month = String(today.getMonth() + 1).padStart(2, "0");
    const year = today.getFullYear();

    return `${day}/${month}/${year}`;
};

const ExpandableDashboardCard = ({
    title,
    total,
    cardKey,
    expandedCard,
    setExpandedCard,
    children
}) => {

    const expanded =
        expandedCard === cardKey;

    return (

        <div
            className={`
                dashboard-card
                dashboard-card-expandable
                ${
                    expanded
                        ? "dashboard-card-expanded"
                        : ""
                }
            `}
            onClick={() =>
                setExpandedCard(
                    expanded
                        ? null
                        : cardKey
                )
            }
        >

            <h3 className="dashboard-card-title">

                <span>
                    {title}
                </span>

                <span className="dashboard-expand-indicator">
                    {expanded ? "▲" : "▼"}
                </span>

            </h3>

            <p>{total}</p>

            {expanded && (

                <div className="dashboard-card-details">

                    {children}

                </div>

            )}

        </div>

    );

};

const isMediaCategory = (category) =>
  category === "Media" ||
  category === "Photos & Videos";

const getCategoryLabel = (category) =>
  category === "Photos & Videos"
    ? "Media"
    : category;

    const getSubcategoryLabel = (subcategory) => {
  if (subcategory === "Science") {
    return "Science & Technology";
  }

  if (
    subcategory === "Cultural" ||
    subcategory === "Culture"
  ) {
    return "Ink and Echo";
  }

  return subcategory;
};

    const getTaxonomyCategory = (category) =>
  CONTENT_TAXONOMY[category] || null;

const getTaxonomySubcategories = (category) =>
  getTaxonomyCategory(category)?.children || {};

const getTaxonomySubcategory = (category, subcategory) =>
  getTaxonomySubcategories(category)?.[subcategory] || null;

const getTaxonomySubSubcategories = (category, subcategory) =>
  getTaxonomySubcategory(category, subcategory)?.children || {};

const getSelectedContentType = (category, subcategory) =>
  getTaxonomySubcategory(category, subcategory)?.contentType ||
  getTaxonomyCategory(category)?.contentType ||
  "article";

const AdminSelect = ({
  name,
  value,
  options = [],
  onChange,
  placeholder = "Select",
  required = false,
  disabled = false,
  className = "",
  ariaLabel
}) => {

  const [isOpen, setIsOpen] =
    useState(false);

  const rootRef =
    useRef(null);

  const triggerRef =
    useRef(null);


  const selectedOption =
    options.find(
      option =>
        String(option.value) ===
        String(value ?? "")
    );


  useEffect(() => {

    if (!isOpen) {
      return;
    }


    const handleOutsideClick =
      event => {

        if (
          rootRef.current &&
          !rootRef.current.contains(
            event.target
          )
        ) {

          setIsOpen(false);

        }

      };


    const handleEscape =
      event => {

        if (event.key === "Escape") {

          setIsOpen(false);

          triggerRef.current?.focus();

        }

      };


    document.addEventListener(
      "mousedown",
      handleOutsideClick
    );

    document.addEventListener(
      "keydown",
      handleEscape
    );


    return () => {

      document.removeEventListener(
        "mousedown",
        handleOutsideClick
      );

      document.removeEventListener(
        "keydown",
        handleEscape
      );

    };

  }, [isOpen]);


  const chooseOption =
    option => {

      onChange?.({
        target: {
          name,
          value:
            String(
              option.value
            )
        }
      });

      setIsOpen(false);

      triggerRef.current?.focus();

    };


  const chooseEmptyOption =
    () => {

      onChange?.({
        target: {
          name,
          value: ""
        }
      });

      setIsOpen(false);

      triggerRef.current?.focus();

    };


  return (

    <div
      ref={rootRef}
      className={`
        admin-custom-select
        ${
          isOpen
            ? "admin-custom-select-open"
            : ""
        }
        ${
          disabled
            ? "admin-custom-select-disabled"
            : ""
        }
        ${className}
      `}
    >

      {/*
       * Hidden native select keeps ordinary
       * form validation behavior intact.
       */}
      <select
        className="admin-custom-select-native"
        name={name}
        value={value ?? ""}
        onChange={onChange}
        required={required}
        disabled={disabled}
        tabIndex={-1}
        aria-hidden="true"
        onInvalid={event => {

          event.preventDefault();

          setIsOpen(true);

          requestAnimationFrame(
            () =>
              triggerRef.current?.focus()
          );

        }}
      >

        {placeholder && (
          <option value="">
            {placeholder}
          </option>
        )}

        {options.map(
          option => (

            <option
              key={String(
                option.value
              )}
              value={option.value}
            >
              {option.label}
            </option>

          )
        )}

      </select>


      <button
        ref={triggerRef}
        type="button"
        className="admin-custom-select-trigger"
        onClick={() =>
          setIsOpen(
            current => !current
          )
        }
        disabled={disabled}
        aria-haspopup="listbox"
        aria-expanded={isOpen}
        aria-label={
          ariaLabel ||
          placeholder
        }
      >

        <span
          className={
            selectedOption
              ? "admin-custom-select-value"
              : "admin-custom-select-placeholder"
          }
        >
          {selectedOption
            ? selectedOption.label
            : placeholder}
        </span>

        <span
          className="admin-custom-select-chevron"
          aria-hidden="true"
        />

      </button>


      {isOpen && (

        <div
          className="admin-custom-select-menu"
          role="listbox"
        >

          {placeholder && (

            <button
              type="button"
              className={`
                admin-custom-select-option
                admin-custom-select-option-placeholder
                ${
                  String(
                    value ?? ""
                  ) === ""
                    ? "admin-custom-select-option-selected"
                    : ""
                }
              `}
              onClick={
                chooseEmptyOption
              }
              role="option"
              aria-selected={
                String(
                  value ?? ""
                ) === ""
              }
            >

              <span>
                {placeholder}
              </span>

            </button>

          )}


          {options.map(
            option => {

              const selected =
                String(
                  option.value
                ) ===
                String(
                  value ?? ""
                );

              return (

                <button
                  key={String(
                    option.value
                  )}
                  type="button"
                  className={`
                    admin-custom-select-option
                    ${
                      selected
                        ? "admin-custom-select-option-selected"
                        : ""
                    }
                  `}
                  onClick={() =>
                    chooseOption(
                      option
                    )
                  }
                  role="option"
                  aria-selected={
                    selected
                  }
                >

                  <span>
                    {option.label}
                  </span>

                  {selected && (
                    <span
                      className="admin-custom-select-check"
                      aria-hidden="true"
                    >
                      ✓
                    </span>
                  )}

                </button>

              );

            }
          )}

        </div>

      )}

    </div>

  );

};

function Admin() {

  const defaultFooterData = {

  publicationName:
    "ছাত্র সংবাদ",

  tagline:
    "Voice of Students • Resistance • Literature • Editorial Thought",

  description: "",

  logo: "/logo.png",

  quickLinks: [

    {
      label: "Home",
      path: "/",
      enabled: true,
      order: 1
    },

    {
      label: "Editorial",
      path: "/editorial",
      enabled: true,
      order: 2
    },

    {
      label: "Articles",
      path: "/articles",
      enabled: true,
      order: 3
    },

    {
      label: "News",
      path: "/news",
      enabled: true,
      order: 4
    },

    {
      label: "Campus Report",
      path: "/campus-report",
      enabled: true,
      order: 5
    },

    {
      label: "Media",
      path: "/photos-videos",
      enabled: true,
      order: 6
    },

    {
      label: "Publications",
      path: "/publications",
      enabled: true,
      order: 7
    },

    {
      label: "About Us",
      path: "/about-us",
      enabled: true,
      order: 8
    }

  ],

  contact: {

    address: "",
    email: "",
    phone: "",
    website: ""

  },

  socialLinks: {

    facebook: "",
    instagram: "",
    threads: "",
    x: "",
    youtube: "",
    linkedin: "",
    telegram: ""

  },

  copyrightText: ""

};

const handleFooterLogoUpload = async (e) => {
  const file = e.target.files?.[0];

  if (!file) {
    return;
  }

  // Only allow image files
  if (!file.type.startsWith("image/")) {
    alert("Please select an image file.");
    e.target.value = "";
    return;
  }

  try {
    const logoFormData = new FormData();

    // Use the same upload mechanism already used
    // by the Admin for cover images.
    logoFormData.append("coverImage", file);

    const response = await axios.post(
      "http://localhost:5000/upload-cover",
      logoFormData
    );

    const uploadedPath = response.data.path;

    if (!uploadedPath) {
      throw new Error(
        "Upload succeeded but no image path was returned."
      );
    }

    // Store the uploaded image path in footerData.logo
    updateFooterField("logo", uploadedPath);

    updateFooterField(
      "logoPublicId",
      response.data.publicId || ""
    );

    alert(
      "Footer logo uploaded successfully. Click Save Footer Settings to apply it."
    );
  } catch (error) {
    console.error(
      "Error uploading footer logo:",
      error
    );

    alert(
      error.response?.data?.message ||
      "Failed to upload footer logo."
    );
  } finally {
    // Allow selecting the same file again later
    e.target.value = "";
  }
};

  const handleFooterSave = async () => {

    try {

      const response =
        await axios.put(
          "http://localhost:5000/api/footer",
          footerData
        );


      setFooterData(
        response.data
      );


      window.dispatchEvent(
        new Event("footerUpdated")
      );


      alert(
        "Footer settings saved successfully!"
      );

    }

    catch (error) {

      console.error(
        "Error saving footer:",
        error
      );


      alert(
        "Failed to save footer settings."
      );

    }

  };

  const updateFooterField =
  (field, value) => {

    setFooterData(prev => ({

      ...prev,

      [field]: value

    }));

  };


const updateFooterContact =
  (field, value) => {

    setFooterData(prev => ({

      ...prev,

      contact: {

        ...prev.contact,

        [field]: value

      }

    }));

  };


const updateFooterSocial =
  (field, value) => {

    setFooterData(prev => ({

      ...prev,

      socialLinks: {

        ...prev.socialLinks,

        [field]: value

      }

    }));

  };

  const updateFooterLink =
  (index, field, value) => {

    setFooterData(prev => {

      const links =
        [...prev.quickLinks];

      links[index] = {

        ...links[index],

        [field]: value

      };

      return {

        ...prev,

        quickLinks: links

      };

    });

  };


const moveFooterLink =
  (index, direction) => {

    setFooterData(prev => {

      const links =
        [...prev.quickLinks];

      const newIndex =
        index + direction;


      if (
        newIndex < 0 ||
        newIndex >= links.length
      ) {

        return prev;

      }


      [
        links[index],
        links[newIndex]

      ] = [

        links[newIndex],
        links[index]

      ];


      return {

        ...prev,

        quickLinks:
          links.map(
            (link, i) => ({

              ...link,

              order: i + 1

            })
          )

      };

    });

  };


  const [footerData, setFooterData] =
    useState(defaultFooterData);


  const [loadingFooter, setLoadingFooter] =
    useState(true);

    const [
  openAdminSections,
  setOpenAdminSections
] = useState({
  postUpload: false,
  publicationUpload: false,
  managePosts: false,
  managePublications: false,
  circleMembers: false,
  aboutUs: false,
  footerSettings: false
});


const scrollToAdminSection = (
  sectionKey
) => {

  /*
   * The panel always remains mounted.
   * Two animation frames give React time
   * to apply its visible state before we
   * calculate the section's page position.
   */
  window.requestAnimationFrame(() => {

    window.requestAnimationFrame(() => {

      const section =
        document.getElementById(
          `admin-section-${sectionKey}`
        );

      if (section) {

        section.scrollIntoView({
          behavior: "smooth",
          block: "start"
        });

      }

    });

  });

};


const openAdminSection = (
  sectionKey
) => {

  setOpenAdminSections(
    previous => ({

      ...previous,

      [sectionKey]: true

    })
  );

  scrollToAdminSection(
    sectionKey
  );

};


const minimizeAdminSection = (
  sectionKey
) => {

  setOpenAdminSections(
    previous => ({

      ...previous,

      [sectionKey]: false

    })
  );

};


const toggleAdminSection = (
  sectionKey
) => {

  if (
    openAdminSections[
      sectionKey
    ]
  ) {

    minimizeAdminSection(
      sectionKey
    );

    return;

  }

  openAdminSection(
    sectionKey
  );

};

    const [
  communityMembers,
  setCommunityMembers
] = useState([]);

const [
  communityMembersLoading,
  setCommunityMembersLoading
] = useState(true);

const [
  communityMembersError,
  setCommunityMembersError
] = useState("");

const [
  communityMembersPage,
  setCommunityMembersPage
] = useState(1);

const [
  communityMembersTotalPages,
  setCommunityMembersTotalPages
] = useState(0);

const [
  communityMembersTotal,
  setCommunityMembersTotal
] = useState(0);

const [
  communityMembersPerPage,
  setCommunityMembersPerPage
] = useState(10);

const [
  communityMembersSearch,
  setCommunityMembersSearch
] = useState("");

const [
  communityMembersSearchInput,
  setCommunityMembersSearchInput
] = useState("");

const [
  communityMemberDeletingId,
  setCommunityMemberDeletingId
] = useState(null);

  const [editingAboutUs, setEditingAboutUs] = useState(false);

  const [aboutUsDraft, setAboutUsDraft] = useState("");

  const startEditingAboutUs = () => {

      setAboutUsDraft(aboutUs.content || "");

      setEditingAboutUs(true);

  };

  const [aboutUs, setAboutUs] = useState({
      content: "",
      editorialTeam: [],
      pastEditors: []
  });

  const [aboutUsLoading, setAboutUsLoading] = useState(true);

  const [newEditorialMember, setNewEditorialMember] =
      useState("");

  const [newPastEditor, setNewPastEditor] =
      useState("");

  const [editingEditorialId, setEditingEditorialId] =
      useState(null);

  const [editingPastEditorId, setEditingPastEditorId] =
      useState(null);

  const [editingEditorialName, setEditingEditorialName] =
      useState("");

  const [editingPastEditorName, setEditingPastEditorName] =
      useState("");

  const fetchAboutUs = async () => {

    try {

        const res = await axios.get(
            "http://localhost:5000/api/about-us"
        );

        setAboutUs(res.data);

    } catch (error) {

        console.log(error);

    } finally {

        setAboutUsLoading(false);

    }

};

const fetchCommunityMembers =
  async ({
    page = communityMembersPage,
    search = communityMembersSearch
  } = {}) => {

    setCommunityMembersLoading(true);
    setCommunityMembersError("");

    try {

      const token =
        localStorage.getItem(
          "adminToken"
        );


      if (!token) {

        throw new Error(
          "Admin authentication required."
        );

      }


      const normalizedSearch =
        typeof search === "string"
          ? search.trim()
          : "";


      const response =
        await axios.get(
          "http://localhost:5000/api/admin/community-members",
          {
            headers: {
              Authorization:
                `Bearer ${token}`
            },

            params: {
              page,

              ...(normalizedSearch
                ? {
                    search:
                      normalizedSearch
                  }
                : {})
            }
          }
        );


      const members =
        Array.isArray(
          response.data?.members
        )
          ? response.data.members
          : [];


      const pagination =
        response.data?.pagination &&
        typeof response.data.pagination ===
          "object"
          ? response.data.pagination
          : {};


      const resolvedPage =
        Number.isSafeInteger(
          pagination.page
        ) &&
        pagination.page > 0
          ? pagination.page
          : 1;


      const resolvedPerPage =
        Number.isSafeInteger(
          pagination.perPage
        ) &&
        pagination.perPage > 0
          ? pagination.perPage
          : 10;


      const resolvedTotalMembers =
        Number.isSafeInteger(
          pagination.totalMembers
        ) &&
        pagination.totalMembers >= 0
          ? pagination.totalMembers
          : members.length;


      const resolvedTotalPages =
        Number.isSafeInteger(
          pagination.totalPages
        ) &&
        pagination.totalPages >= 0
          ? pagination.totalPages
          : 0;


      setCommunityMembers(
        members
      );

      setCommunityMembersPage(
        resolvedPage
      );

      setCommunityMembersPerPage(
        resolvedPerPage
      );

      setCommunityMembersTotal(
        resolvedTotalMembers
      );

      setCommunityMembersTotalPages(
        resolvedTotalPages
      );

      setCommunityMembersSearch(
        typeof pagination.search ===
          "string"
          ? pagination.search
          : normalizedSearch
      );

    } catch (error) {

      console.error(
        "Error loading Circle members:",
        error
      );


      setCommunityMembersError(
        error.response?.data?.message ||
        "Unable to load Circle members."
      );

    } finally {

      setCommunityMembersLoading(
        false
      );

    }

  };

  const handleCommunityMembersSearch =
  async (event) => {

    event.preventDefault();


    if (communityMembersLoading) {
      return;
    }


    const normalizedSearch =
      communityMembersSearchInput.trim();


    /*
     * Keep the visible input consistent
     * with the normalized backend search.
     */
    setCommunityMembersSearchInput(
      normalizedSearch
    );


    /*
     * Every new search begins on page 1.
     */
    await fetchCommunityMembers({
      page: 1,
      search: normalizedSearch
    });

  };

  const clearCommunityMembersSearch =
  async () => {

    if (communityMembersLoading) {
      return;
    }


    setCommunityMembersSearchInput(
      ""
    );


    /*
     * Clearing a search returns the Admin
     * to page 1 of the complete member list.
     */
    await fetchCommunityMembers({
      page: 1,
      search: ""
    });

  };

  const goToCommunityMembersPage =
  async (page) => {

    if (communityMembersLoading) {
      return;
    }


    if (
      !Number.isSafeInteger(page) ||
      page < 1
    ) {
      return;
    }


    /*
     * Do not request a page beyond the
     * currently known result set.
     */
    if (
      communityMembersTotalPages > 0 &&
      page > communityMembersTotalPages
    ) {
      return;
    }


    /*
     * Pagination must preserve the search
     * that is currently applied.
     */
    await fetchCommunityMembers({
      page,
      search:
        communityMembersSearch
    });

  };

  const removeCommunityMember =
  async (member) => {

    if (
      !member?._id ||
      communityMemberDeletingId
    ) {
      return;
    }


    const confirmed =
      window.confirm(
        `Remove ${
          member.name ||
          member.email
        } from the Circle?\n\nThis action cannot be undone.`
      );


    if (!confirmed) {
      return;
    }


    setCommunityMemberDeletingId(
      member._id
    );


    try {

      const token =
        localStorage.getItem(
          "adminToken"
        );


      if (!token) {

        throw new Error(
          "Admin authentication required."
        );

      }


      await axios.delete(
        `http://localhost:5000/api/admin/community-members/${member._id}`,
        {
          headers: {
            Authorization:
              `Bearer ${token}`
          }
        }
      );


      /*
       * Do not manually remove only the
       * deleted card from local state.
       *
       * Re-fetch the current server page so
       * totals, search results and pagination
       * all stay authoritative.
       *
       * If deletion removes the final member
       * from the final page, the backend will
       * clamp this requested page to the new
       * last valid page automatically.
       */
      await fetchCommunityMembers({
        page:
          communityMembersPage,
        search:
          communityMembersSearch
      });


      alert(
        "Circle member removed successfully."
      );

    } catch (error) {

      console.error(
        "Error removing Circle member:",
        error
      );


      alert(
        error.response?.data?.message ||
        "Unable to remove this Circle member."
      );

    } finally {

      setCommunityMemberDeletingId(
        null
      );

    }

  };

  const [formData, setFormData] = useState({
  title: "",
  content: "",
  category: "",
  subcategory: "",
  subSubcategory: "",
  writer: "",
  publishDate: getTodayDate(),
  readingTime: "",
  image: "",
  coverImage: "",
  video: "",
  type: "article",
  featured: false
});

  const [publicationData, setPublicationData] = useState({
    type: "newspaper",
    title: "",
    author: "",
    month: "January",
    year: new Date().getFullYear(),
  });

  const [publications, setPublications] = useState([]);
  const [loadingPublications, setLoadingPublications] = useState(true);
  const [publicationCategory, setPublicationCategory] = useState("");

  const [publicationCurrentPage, setPublicationCurrentPage] = useState(1);

  const publicationsPerPage = 10;

  const filteredPublications =
    publicationCategory === ""
        ? publications
        : publications.filter(
              publication =>
                  publication.type === publicationCategory
          );

  const publicationTotalPages = Math.ceil(
      filteredPublications.length / publicationsPerPage
  );

  const publicationIndexOfLast =
      publicationCurrentPage * publicationsPerPage;

  const publicationIndexOfFirst =
      publicationIndexOfLast - publicationsPerPage;

  const currentPublications =
      filteredPublications.slice(
          publicationIndexOfFirst,
          publicationIndexOfLast
      );

  const months = [
    "January", "February", "March", "April",
    "May", "June", "July", "August",
    "September", "October", "November", "December"
  ];

  const currentYear = new Date().getFullYear();
  const years = [];

  for (let y = currentYear; y >= 2020; y--) {
    years.push(y);
  }

  const [publicationPdf, setPublicationPdf] = useState(null);
  const [publicationCoverImage, setPublicationCoverImage] = useState(null);
  const publicationPdfInputRef = useRef(null);
  const publicationCoverImageInputRef = useRef(null);
  const coverImageInputRef = useRef(null);
  const footerLogoInputRef = useRef(null);

  const photoInputRef = useRef(null);

/*
 * Unified photo-manager state.
 *
 * Each item will eventually represent either:
 * - an existing published gallery image, or
 * - a newly selected local image.
 *
 * Array order = public gallery order.
 */
const [photoItems, setPhotoItems] =
  useState([]);

/*
 * Stores the unique ID of whichever
 * photo is currently selected as cover.
 */
const [photoCoverId, setPhotoCoverId] =
  useState(null);

  /*
 * Used only while a photo is being
 * reordered with drag-and-drop.
 */
const [
  draggedPhotoId,
  setDraggedPhotoId
] = useState(null);

const handlePhotoFilesSelect = (
  event
) => {

  const selectedFiles =
    Array.from(
      event.target.files || []
    );

  /*
   * Always clear the native file input.
   *
   * This allows the same file to be
   * selected again later if the Admin
   * first removes it from the manager.
   */
  event.target.value = "";


  if (selectedFiles.length === 0) {
    return;
  }


  /*
   * Browser accept="image/*" helps the
   * picker, but we still validate here.
   */
  const invalidFile =
    selectedFiles.find(
      file =>
        !file.type.startsWith(
          "image/"
        )
    );


  if (invalidFile) {

    alert(
      `${invalidFile.name} is not a valid image file.`
    );

    return;
  }


  const oversizedFile =
    selectedFiles.find(
      file =>
        file.size >
        MAX_IMAGE_SIZE
    );


  if (oversizedFile) {

    alert(
      `${oversizedFile.name} is ` +
      `${(
        oversizedFile.size /
        1024 /
        1024
      ).toFixed(2)} MB. ` +
      "Maximum allowed size is 3 MB per image."
    );

    return;
  }


  /*
   * Signatures of local files already
   * selected during this Admin session.
   */
  const existingSignatures =
    new Set(
      photoItems
        .filter(
          item =>
            item.source === "new" &&
            item.file
        )
        .map(
          item =>
            getPhotoFileSignature(
              item.file
            )
        )
    );


  /*
   * This second Set also catches an
   * accidental duplicate inside the
   * same newly-selected batch.
   */
  const batchSignatures =
    new Set();


  const duplicateNames = [];


  const uniqueFiles =
    selectedFiles.filter(
      file => {

        const signature =
          getPhotoFileSignature(
            file
          );


        if (
          existingSignatures.has(
            signature
          ) ||
          batchSignatures.has(
            signature
          )
        ) {

          duplicateNames.push(
            file.name
          );

          return false;
        }


        batchSignatures.add(
          signature
        );

        return true;

      }
    );


  if (uniqueFiles.length === 0) {

    alert(
      "The selected photo" +
      (
        selectedFiles.length === 1
          ? " is"
          : "s are"
      ) +
      " already in this photo list."
    );

    return;
  }


  const remainingSlots =
    10 - photoItems.length;


  /*
   * Do not silently accept only part
   * of the batch.
   */
  if (
    uniqueFiles.length >
    remainingSlots
  ) {

    alert(
      photoItems.length === 0
        ? "You can upload a maximum of 10 photos per post."
        : (
            `You already have ${photoItems.length} ` +
            `${
              photoItems.length === 1
                ? "photo"
                : "photos"
            }. ` +
            `You can add only ${remainingSlots} more.`
          )
    );

    return;
  }


  const newItems =
    uniqueFiles.map(
      file => ({

        id:
          createPhotoItemId(),

        source:
          "new",

        file,

        url:
          "",

        publicId:
          "",

        previewUrl:
          URL.createObjectURL(
            file
          )

      })
    );


  const nextPhotoItems = [
    ...photoItems,
    ...newItems
  ];


  setPhotoItems(
    nextPhotoItems
  );


  /*
   * Only the very first photo becomes
   * the automatic initial cover.
   *
   * Later selections never reset an
   * already chosen cover.
   */
  if (!photoCoverId) {

    setPhotoCoverId(
      nextPhotoItems[0]?.id ||
      null
    );

  }


  if (duplicateNames.length > 0) {

    alert(
      `${
        duplicateNames.length
      } duplicate ${
        duplicateNames.length === 1
          ? "photo was"
          : "photos were"
      } skipped.`
    );

  }

};

const removePhotoItem = (
  photoId
) => {

  const itemToRemove =
    photoItems.find(
      item =>
        item.id === photoId
    );


  if (!itemToRemove) {
    return;
  }


  /*
   * Blob preview URLs exist only for
   * newly selected local files.
   */
  if (
    itemToRemove.source === "new" &&
    itemToRemove.previewUrl
  ) {

    URL.revokeObjectURL(
      itemToRemove.previewUrl
    );

  }


  const nextPhotoItems =
    photoItems.filter(
      item =>
        item.id !== photoId
    );


  setPhotoItems(
    nextPhotoItems
  );


  /*
   * If the cover was removed, the
   * first remaining photo becomes the
   * new automatic cover.
   */
  if (
    photoCoverId === photoId
  ) {

    setPhotoCoverId(
      nextPhotoItems[0]?.id ||
      null
    );

  }

};

const movePhotoItem = (
  photoId,
  direction
) => {

  setPhotoItems(
    currentItems => {

      const currentIndex =
        currentItems.findIndex(
          item =>
            item.id === photoId
        );


      if (currentIndex === -1) {
        return currentItems;
      }


      const targetIndex =
        direction === "left"
          ? currentIndex - 1
          : currentIndex + 1;


      if (
        targetIndex < 0 ||
        targetIndex >=
          currentItems.length
      ) {
        return currentItems;
      }


      const nextItems = [
        ...currentItems
      ];


      [
        nextItems[currentIndex],
        nextItems[targetIndex]
      ] = [
        nextItems[targetIndex],
        nextItems[currentIndex]
      ];


      return nextItems;

    }
  );

};

const handlePhotoDragStart = (
  photoId
) => {

  setDraggedPhotoId(
    photoId
  );

};


const handlePhotoDrop = (
  targetPhotoId
) => {

  if (
    !draggedPhotoId ||
    draggedPhotoId ===
      targetPhotoId
  ) {

    setDraggedPhotoId(
      null
    );

    return;
  }


  setPhotoItems(
    currentItems => {

      const fromIndex =
        currentItems.findIndex(
          item =>
            item.id ===
              draggedPhotoId
        );


      const toIndex =
        currentItems.findIndex(
          item =>
            item.id ===
              targetPhotoId
        );


      if (
        fromIndex === -1 ||
        toIndex === -1
      ) {

        return currentItems;
      }


      const nextItems = [
        ...currentItems
      ];


      const [
        movedItem
      ] = nextItems.splice(
        fromIndex,
        1
      );


      /*
       * Move the dragged photo to the
       * target photo's current position.
       */
      nextItems.splice(
        toIndex,
        0,
        movedItem
      );


      return nextItems;

    }
  );


  setDraggedPhotoId(
    null
  );

};

  const [cropFile, setCropFile] = useState(null);
  const [showCropModal, setShowCropModal] = useState(false);
  const [cropTarget, setCropTarget] = useState("post");
  const [cropAspect, setCropAspect] = useState(1);

  const [allPosts, setAllPosts] = useState([]);

  const [searchTerm, setSearchTerm] = useState("");

  const [selectedCategory, setSelectedCategory] =
  useState("");

  const [sortOption, setSortOption] =
  useState("newest");

  const [latestIndex, setLatestIndex] = useState(0);
  const [acrossIndex, setAcrossIndex] = useState(0);

  const [currentPage, setCurrentPage] = useState(1);

  const postsPerPage = 10;

  const [editId, setEditId] = useState(null);

  const [showDeleteAllModal, setShowDeleteAllModal] =
    useState(false);

  const [showFinalDeleteModal, setShowFinalDeleteModal] =
      useState(false);

  const [deleteConfirmation, setDeleteConfirmation] =
      useState("");

  const [selectedPosts, setSelectedPosts] = useState([]);
  const [selectedPublications, setSelectedPublications] = useState([]);

  const [showDeletePopup, setShowDeletePopup] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState([]);
  const [deleteType, setDeleteType] = useState("post");

  const [expandedCard, setExpandedCard] = useState(null);

  const handlePublicationSelection = (id) => {

    setSelectedPublications(prev => {

        if (prev.includes(id)) {

            return prev.filter(
                publicationId =>
                    publicationId !== id
            );

        }

        return [
            ...prev,
            id
        ];

    });

};

const handleSelectAllPublications = (e) => {

    if (e.target.checked) {

        setSelectedPublications(
            publications.map(pub => pub._id)
        );

    }

    else {

        setSelectedPublications([]);

    }

};

  const selectedContentType = getSelectedContentType(
  formData.category,
  formData.subcategory
);

const isPhoto = selectedContentType === "photo";

const isVideo = selectedContentType === "video";

const showCoverImage =
  isVideo ||
  (
    !isPhoto &&
    !isVideo &&
    !isMediaCategory(formData.category)
  );

  const fetchPosts = async () => {

      try {

          const res = await axios.get(
              "http://localhost:5000/posts"
          );

          setAllPosts(res.data);

      }

      catch (err) {

          console.log(err);

      }

  };

    useEffect(() => {

        setPublicationCurrentPage(1);

    }, [publicationCategory]);

    useEffect(() => {

    // Fetch posts
    fetchPosts();

    // Fetch first Circle-member page
fetchCommunityMembers({
  page: 1,
  search: ""
});

    // Fetch publications
    axios
      .get("http://localhost:5000/api/publications")
      .then((res) => {
        setPublications(res.data);
        setLoadingPublications(false);
      })
      .catch((err) => {
        console.log(err);
        setLoadingPublications(false);
      });

    // Fetch About Us
    axios
      .get("http://localhost:5000/api/about-us")
      .then((res) => {
        setAboutUs(res.data);
        setAboutUsLoading(false);
      })
      .catch((err) => {
        console.error("Error loading About Us:", err);
        setAboutUsLoading(false);
      });

}, []);

    useEffect(() => {

      const fetchFooter =
        async () => {

          try {

            const response =
              await axios.get(
                "http://localhost:5000/api/footer"
              );

            setFooterData(
              response.data
            );

          }

          catch (error) {

            console.error(
              "Error loading footer:",
              error
            );

          }

          finally {

            setLoadingFooter(false);

          }

        };


      fetchFooter();

    }, []);

  const openDeletePopup = (ids) => {

      console.log("Delete Target:", ids);

      setDeleteTarget(ids);

      setShowDeletePopup(true);

  };

  const clearPhotoManager = () => {

  photoItems.forEach(
    item => {

      if (
        item.source === "new" &&
        item.previewUrl
      ) {

        URL.revokeObjectURL(
          item.previewUrl
        );

      }

    }
  );


  setPhotoItems([]);

  setPhotoCoverId(null);

  setDraggedPhotoId(null);


  if (photoInputRef.current) {
    photoInputRef.current.value = "";
  }

};

  const handleChange = (e) => {
  const { name, value, type, checked } = e.target;

  if (name === "category") {

    if (isPhoto) {
  clearPhotoManager();
}
    setFormData({
      ...formData,
      category: value,
      subcategory: "",
      subSubcategory: "",
      publishDate: getTodayDate(),
      coverImage: "",
    });

    if (coverImageInputRef.current) {
      coverImageInputRef.current.value = "";
    }
  } else if (name === "subcategory") {

    const nextContentType =
  getSelectedContentType(
    formData.category,
    value
  );


if (
  isPhoto &&
  nextContentType !== "photo"
) {

  clearPhotoManager();

}
    setFormData({
      ...formData,
      subcategory: value,
      subSubcategory: "",
    });
  } else {
    setFormData({
      ...formData,
      [name]: type === "checkbox"
        ? checked
        : value
    });
  }
};

const handleCoverImageSelect = (e) => {

  const file = e.target.files?.[0];

  if (!file) {
    return;
  }

  if (!file.type.startsWith("image/")) {

    alert("Please select an image file.");

    e.target.value = "";

    return;

  }

  if (file.size > MAX_IMAGE_SIZE) {

    alert(
      `Image size is ${(file.size / 1024 / 1024).toFixed(2)} MB. Maximum allowed size is 3 MB.`
    );

    e.target.value = "";

    return;

  }

  setCropFile(file);
  setShowCropModal(true);

};

const handlePublicationCoverSelect = (e) => {

  const file = e.target.files?.[0];

  if (!file) {
    return;
  }

  if (!file.type.startsWith("image/")) {

    alert("Please select an image file.");

    e.target.value = "";

    return;

  }

  if (file.size > MAX_IMAGE_SIZE) {

  alert(
    `Image size is ${(file.size / 1024 / 1024).toFixed(2)} MB. Maximum allowed size is 3 MB.`
  );

  e.target.value = "";

  return;

}

  // Newspaper keeps its existing upload behavior.
  if (publicationData.type === "newspaper") {

    setPublicationCoverImage(file);

    return;

  }

  // Books and Magazines use the square cropper.
  setCropTarget("publication");
  setCropAspect(1);

  setCropFile(file);
  setShowCropModal(true);

};

  const isPdfCategory = false;

  const confirmDelete = async () => {

   try {

    if (deleteType === "publication") {

        if (deleteTarget.length === 1) {

            await axios.delete(
                `http://localhost:5000/api/publications/${deleteTarget[0]}`
            );

        }
        else {

            await axios.post(
                "http://localhost:5000/api/publications/delete-many",
                {
                    ids: deleteTarget
                }
            );

        }

        // Remove deleted publications immediately from Admin UI
        setPublications(prev =>
            prev.filter(
                publication =>
                    !deleteTarget.includes(
                        publication._id.toString()
                    )
            )
        );

        // Tell App that publications changed
        window.dispatchEvent(
            new Event("publicationsUpdated")
        );

        // Clear publication selection
        setSelectedPublications([]);

    }

    else {

        await axios.post(
            "http://localhost:5000/delete-posts",
            {
                ids: deleteTarget
            }
        );

        // Refresh Admin post list
        await fetchPosts();

        // Tell App that posts changed
        window.dispatchEvent(
            new Event("postsUpdated")
        );

        // Clear post selection
        setSelectedPosts([]);

    }

    // Reset delete state
    setDeleteTarget([]);

    setDeleteType("post");

    setShowDeletePopup(false);

}
catch (error) {

    console.log(error);

}}

const saveAboutUsContent = async () => {

    try {

        const res = await axios.put(
            "http://localhost:5000/api/about-us",
            {
                content: aboutUsDraft,
                editorialTeam: aboutUs.editorialTeam,
                pastEditors: aboutUs.pastEditors
            }
        );

        setAboutUs(res.data);

        setAboutUsDraft("");

        setEditingAboutUs(false);

        alert("About Us updated successfully.");

    } catch (error) {

        console.error(error);

        alert("Error updating About Us.");

    }

};

const cancelAboutUsEdit = () => {

    setAboutUsDraft("");

    setEditingAboutUs(false);

};

const deleteAboutUsContent = async () => {

    if (
        !window.confirm(
            "Are you sure you want to delete the About Us content?"
        )
    ) {
        return;
    }

    try {

        const res = await axios.put(
            "http://localhost:5000/api/about-us",
            {
                content: "",
                editorialTeam: aboutUs.editorialTeam,
                pastEditors: aboutUs.pastEditors
            }
        );

        setAboutUs(res.data);

        setAboutUsDraft("");

        setEditingAboutUs(false);

        alert("About Us content deleted successfully.");

    } catch (error) {

        console.error(error);

        alert("Error deleting About Us content.");

    }

};

const addEditorialMember = async () => {

    const name = newEditorialMember.trim();

    if (!name) return;

    try {

        const res = await axios.post(
            "http://localhost:5000/api/about-us/editorial-team",
            {
                name
            }
        );

        setAboutUs(res.data);
        setNewEditorialMember("");

    } catch (error) {

        console.error(error);

        alert("Error adding editorial team member.");

    }

};

const addPastEditor = async () => {

    const name = newPastEditor.trim();

    if (!name) return;

    try {

        const res = await axios.post(
            "http://localhost:5000/api/about-us/past-editors",
            {
                name
            }
        );

        setAboutUs(res.data);
        setNewPastEditor("");

    } catch (error) {

        console.error(error);

        alert("Error adding past editor.");

    }

};

const saveEditorialMember = async (id) => {

    const name = editingEditorialName.trim();

    if (!name) return;

    try {

        const res = await axios.put(
            `http://localhost:5000/api/about-us/editorial-team/${id}`,
            {
                name
            }
        );

        setAboutUs(res.data);

        setEditingEditorialId(null);
        setEditingEditorialName("");

    } catch (error) {

        console.error(error);

        alert("Error updating editorial team member.");

    }

};

const savePastEditor = async (id) => {

    const name = editingPastEditorName.trim();

    if (!name) return;

    try {

        const res = await axios.put(
            `http://localhost:5000/api/about-us/past-editors/${id}`,
            {
                name
            }
        );

        setAboutUs(res.data);

        setEditingPastEditorId(null);
        setEditingPastEditorName("");

    } catch (error) {

        console.error(error);

        alert("Error updating past editor.");

    }

};

const deleteEditorialMember = async (id) => {

    if (!window.confirm(
        "Are you sure you want to delete this member?"
    )) {
        return;
    }

    try {

        const res = await axios.delete(
            `http://localhost:5000/api/about-us/editorial-team/${id}`
        );

        setAboutUs(res.data);

    } catch (error) {

        console.error(error);

        alert("Error deleting editorial team member.");

    }

};

const deletePastEditor = async (id) => {

    if (!window.confirm(
        "Are you sure you want to delete this past editor?"
    )) {
        return;
    }

    try {

        const res = await axios.delete(
            `http://localhost:5000/api/about-us/past-editors/${id}`
        );

        setAboutUs(res.data);

    } catch (error) {

        console.error(error);

        alert("Error deleting past editor.");

    }

};

const moveMember = async (teamName, index, direction) => {

    const team = [...aboutUs[teamName]];

    const newIndex =
        direction === "up"
            ? index - 1
            : index + 1;

    if (
        newIndex < 0 ||
        newIndex >= team.length
    ) {
        return;
    }

    // Swap members
    [team[index], team[newIndex]] =
        [team[newIndex], team[index]];

    const ids = team.map(member => member._id);

    try {

        const res = await axios.put(
            "http://localhost:5000/api/about-us/reorder",
            {
                type: teamName,
                ids
            }
        );

        setAboutUs(res.data);

    } catch (error) {

        console.error(error);

        alert("Error changing member order.");

    }

};

const handleEdit = (post) => {

  clearPhotoManager();

  const editingPhotoPost =
  (
    post.category === "Media" ||
    post.category ===
      "Photos & Videos"
  ) &&
  post.subcategory ===
    "Photos";


  /*
   * Prefer the modern images[] gallery.
   *
   * Older single-photo posts may only
   * have image / imagePublicId, so keep
   * them editable too.
   */
  const existingGallery =
  editingPhotoPost
    ? (
        Array.isArray(
          post.images
        ) &&
        post.images.length > 0
          ? post.images
              .filter(
                imageItem =>
                  imageItem?.url
              )
              .slice(0, 10)
          : (
              post.image
                ? [
                    {
                      url:
                        post.image,
                      publicId:
                        post.imagePublicId ||
                        ""
                    }
                  ]
                : []
            )
      )
    : [];


  const existingPhotoItems =
    existingGallery.map(
      imageItem => ({

        id:
          createPhotoItemId(),

        source:
          "existing",

        file:
          null,

        url:
          imageItem.url ||
          "",

        publicId:
          imageItem.publicId ||
          "",

        previewUrl:
          ""

      })
    );


  /*
   * Find the existing cover.
   *
   * Prefer publicId matching, but URL
   * matching also preserves older posts
   * that do not have Cloudinary IDs.
   */
  const existingCoverItem =
    existingPhotoItems.find(
      item =>
        (
          post.imagePublicId &&
          item.publicId ===
            post.imagePublicId
        ) ||
        (
          post.image &&
          item.url ===
            post.image
        )
    ) ||
    existingPhotoItems[0] ||
    null;


  setPhotoItems(
    existingPhotoItems
  );

  setPhotoCoverId(
    existingCoverItem?.id ||
    null
  );

  setDraggedPhotoId(null);

  setFormData({
    title: post.title,
    content: post.content,
    category:
    post.category === "Photos & Videos"
      ? "Media"
      : post.category,

    subcategory:
post.subcategory === "Science"
  ? "Science & Technology"
  : post.subcategory === "Cultural"
  ? "Ink and Echo"
  : post.subcategory || "",

subSubcategory: post.subSubcategory || "",

writer: post.writer || "",
    readingTime: post.readingTime || "",

    image: post.image || "",
    coverImage: post.coverImage || "",
    video: post.video || "",
    type: post.type || "article",
    publishDate: post.publishDate || getTodayDate(),
    featured: post.featured
});

  setEditId(post._id);

  /*
   * Editing a post should never close
   * Manage Posts. It only ensures that
   * Post Upload is visible and brings the
   * loaded edit form into view.
   */
  openAdminSection(
    "postUpload"
  );

};

const handleArticleSubmit = async () => {

  let updatedFormData = { ...formData };

  updatedFormData.type =
  selectedContentType === "video"
    ? "video"
    : "article";

  // Upload cover image if selected
  if (formData.coverImage instanceof File) {

      const coverData = new FormData();

      coverData.append("coverImage", formData.coverImage);

      const response = await axios.post(
          "http://localhost:5000/upload-cover",
          coverData
      );

      updatedFormData.coverImage =
        response.data.path;

      updatedFormData.coverImagePublicId =
        response.data.publicId;
  }

  if (editId) {

    await axios.put(
      `http://localhost:5000/update-post/${editId}`,
      updatedFormData
    );

    alert("Post Updated!");

  } else {

    await axios.post(
      "http://localhost:5000/add-post",
      updatedFormData
    );

    window.dispatchEvent(
      new Event("postsUpdated")
    );

    alert("Post Added Successfully!");

  }

  setFormData({
  title: "",
  content: "",
  category: "",
  subcategory: "",
  subSubcategory: "",
  writer: "",
  publishDate: getTodayDate(),
  readingTime: "",
  image: "",
  coverImage: "",
  video: "",
  type: "article",
  featured: false
});

  setEditId(null);

  window.location.reload();

};

const handleSubmit = async (e) => {

  e.preventDefault();

  try {

    // PHOTO UPLOAD
if (isPhoto) {

  /*
   * The photo manager itself is now
   * authoritative.
   */
  if (photoItems.length === 0) {

    alert(
      "Please keep at least one photo in the gallery."
    );

    return;
  }


  const coverIndex =
    photoItems.findIndex(
      item =>
        item.id ===
        photoCoverId
    );


  if (coverIndex === -1) {

    alert(
      "Please select a cover photo."
    );

    return;
  }


  const newPhotoItems =
    photoItems.filter(
      item =>
        item.source === "new"
    );


  const invalidNewPhoto =
    newPhotoItems.find(
      item =>
        !(item.file instanceof File)
    );


  if (invalidNewPhoto) {

    alert(
      "One of the newly selected photos is unavailable. Please remove it and select it again."
    );

    return;
  }


  const data =
    new FormData();


  data.append(
    "title",
    formData.title
  );

  data.append(
    "content",
    formData.content
  );

  data.append(
    "featured",
    formData.featured
  );

  data.append(
    "publishDate",
    formData.publishDate
  );

  /*
   * The cover is an index in the FINAL
   * gallery order, not necessarily the
   * first photo.
   */
  data.append(
    "coverIndex",
    String(coverIndex)
  );


  /*
   * Upload new files in their current
   * relative gallery order.
   */
  newPhotoItems.forEach(
    item => {

      data.append(
        "images",
        item.file
      );

    }
  );


  if (editId) {

    /*
     * Describe the complete desired
     * final gallery.
     *
     * Existing images are identified
     * using data already belonging to
     * this post.
     *
     * New images refer to their index
     * inside the newly-uploaded files.
     */
    let nextUploadIndex = 0;


    const galleryLayout =
      photoItems.map(
        item => {

          if (
            item.source ===
            "existing"
          ) {

            return {
              source:
                "existing",

              publicId:
                item.publicId ||
                "",

              url:
                item.url ||
                ""
            };

          }


          const layoutItem = {
            source:
              "new",

            uploadIndex:
              nextUploadIndex
          };


          nextUploadIndex += 1;


          return layoutItem;

        }
      );


    data.append(
      "galleryLayout",
      JSON.stringify(
        galleryLayout
      )
    );


    await axios.put(
      `http://localhost:5000/update-image/${editId}`,
      data
    );


    alert(
      "Photo updated successfully!"
    );

  } else {

    /*
     * A new post contains only new
     * images, already appended above
     * in final gallery order.
     */
    await axios.post(
      "http://localhost:5000/add-image",
      data
    );


    alert(
      "Photo uploaded successfully!"
    );

  }


  setEditId(null);

  window.location.reload();

  return;
}

    // EVERYTHING ELSE
    await handleArticleSubmit();

  }

  catch (error) {
    console.log(error);

    setFormData({
        ...formData,
        publishDate: getTodayDate(),
        coverImage: "",
    });

    if (coverImageInputRef.current) {
        coverImageInputRef.current.value = "";
    }

    alert("Error adding post");
}

};

const handlePublicationSubmit = async (e) => {
  e.preventDefault();

  try {
    const form = new FormData();

    // Auto-generate title based on type
    let generatedTitle = publicationData.title;

    if (publicationData.type === "newspaper") {
      generatedTitle = `ছাত্র সংবাদ ${publicationData.month} ${publicationData.year}`;
    }

    if (publicationData.type === "magazine") {
      generatedTitle = `ছাত্র সংবাদ Conference Edition ${publicationData.year}`;
    }

    const finalData = {
      ...publicationData,
      title: generatedTitle,
    };

    Object.keys(finalData).forEach((key) => {
      form.append(key, finalData[key]);
    });

    if (publicationPdf) {
      form.append("pdf", publicationPdf);
    }

    if (publicationCoverImage) {
      form.append("coverImage", publicationCoverImage);
    }

    const res = await axios.post(
      "http://localhost:5000/api/publications",
      form,
      {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      }
    );

    console.log(res.data);
    alert("Publication uploaded successfully!");

    // Refresh publication list in admin table
    const updated = await axios.get("http://localhost:5000/api/publications");
    setPublications(updated.data);

    // Tell the main App that publications changed
    window.dispatchEvent(
      new Event("publicationsUpdated")
    );

    setPublicationData({
      type: "newspaper",
      title: "",
      author: "",
      month: "January",
      year: new Date().getFullYear(),
    });

    setPublicationPdf(null);
    setPublicationCoverImage(null);

    if (publicationPdfInputRef.current) {
      publicationPdfInputRef.current.value = "";
    }

    if (publicationCoverImageInputRef.current) {
      publicationCoverImageInputRef.current.value = "";
    }
  } catch (error) {
    console.error(error);

    // Show backend validation message
    if (error.response?.data?.message) {
      alert(error.response.data.message);
    } else {
      alert("Error uploading publication");
    }

    setPublicationPdf(null);
    setPublicationCoverImage(null);

    if (publicationPdfInputRef.current) {
      publicationPdfInputRef.current.value = "";
    }

    if (publicationCoverImageInputRef.current) {
      publicationCoverImageInputRef.current.value = "";
    }
  }
};

const handleDeletePublication = (id) => {

    setDeleteTarget([id]);

    setDeleteType("publication");

    setShowDeletePopup(true);

};

  const handleDelete = async (id) => {

  const confirmDelete = window.confirm(
    "Are you sure you want to delete this post?"
  );

  if (!confirmDelete) return;

  try {

    await axios.delete(
      `http://localhost:5000/delete-post/${id}`
    );

    alert("Post deleted");

    window.location.reload();

  } catch (error) {

    console.log(error);

    alert("Error deleting post");

  }

};

const handleCancelEdit = () => {

  setFormData({
  title: "",
  content: "",
  category: "",
  subcategory: "",
  subSubcategory: "",
  writer: "",
  publishDate: getTodayDate(),
  readingTime: "",
  image: "",
  coverImage: "",
  video: "",
  type: "article",
  featured: false
});

clearPhotoManager();

setEditId(null);

};

/*const handleDeleteEverything = async () => {

  try {

    await axios.delete(
      "http://localhost:5000/delete-all-posts"
    );

    alert("All posts deleted successfully.");

    setShowFinalDeleteModal(false);

    setShowDeleteAllModal(false);

    setDeleteConfirmation("");

    window.location.reload();

  }

  catch (error) {

    console.log(error);

    alert("Error deleting posts.");

  }

};*/

  const handleLogout = () => {

    localStorage.removeItem(
      "adminToken"
    );

    localStorage.removeItem(
      "isAdmin"
    );

    window.location.replace(
      "/login"
    );

  };

  const filteredPosts = allPosts.filter((post) => {

  const matchesSearch =
    post.title
      .toLowerCase()
      .includes(searchTerm.toLowerCase());

  const matchesCategory =
    selectedCategory === "" ||
    (
      selectedCategory === "Media"
        ? isMediaCategory(post.category)
        : post.category === selectedCategory
    );

  return (
    matchesSearch &&
    matchesCategory
  );

});

const sortedPosts = [...filteredPosts];

if (sortOption === "newest") {

  sortedPosts.sort(
    (a, b) =>
      new Date(b.createdAt) -
      new Date(a.createdAt)
  );

}

if (sortOption === "oldest") {

  sortedPosts.sort(
    (a, b) =>
      new Date(a.createdAt) -
      new Date(b.createdAt)
  );

}

if (sortOption === "az") {

  sortedPosts.sort(
    (a, b) =>
      a.title.localeCompare(b.title)
  );

}

if (sortOption === "za") {

  sortedPosts.sort(
    (a, b) =>
      b.title.localeCompare(a.title)
  );

}

if (sortOption === "featured") {

  sortedPosts.sort(
    (a, b) =>
      b.featured - a.featured
  );

}

  const indexOfLastPost =
    currentPage * postsPerPage;

  const indexOfFirstPost =
    indexOfLastPost - postsPerPage;

  const currentPosts =
    sortedPosts.slice(
      indexOfFirstPost,
      indexOfLastPost
    );



  const dashboardStats = {

    total: allPosts.length,

    featured:
      allPosts.filter(
        p => p.featured
      ).length,

    editorial:
      allPosts.filter(
        p => p.category === "Editorial"
      ).length,

    articles:
      allPosts.filter(
        p => p.category === "Articles"
      ).length,

    articleInternational:
      allPosts.filter(
        p =>
          p.category === "Articles" &&
          p.subcategory === "International"
      ).length,

    articleNational:
      allPosts.filter(
        p =>
          p.category === "Articles" &&
          p.subcategory === "National"
      ).length,

    articleState:
      allPosts.filter(
        p =>
          p.category === "Articles" &&
          p.subcategory === "State"
      ).length,

    articleSports:
      allPosts.filter(
        p =>
          p.category === "Articles" &&
          p.subcategory === "Sports"
      ).length,

    articleScience:
      allPosts.filter(
        p =>
          p.category === "Articles" &&
          (
            p.subcategory === "Science" ||
            p.subcategory === "Science & Technology"
          )
      ).length,

    articleEnvironment:
      allPosts.filter(
        p =>
          p.category === "Articles" &&
          p.subcategory === "Environment"
      ).length,

    articleHerSpace:
      allPosts.filter(
        p =>
          p.category === "Articles" &&
          p.subcategory === "Her Space"
      ).length,

    articleTribalVoices:
      allPosts.filter(
        p =>
          p.category === "Articles" &&
          p.subcategory === "Tribal Voices"
      ).length,

    articleInkAndEcho:
  allPosts.filter(
    p =>
      p.category === "Articles" &&
      (
        p.subcategory === "Cultural" ||
        p.subcategory === "Culture" ||
        p.subcategory === "Ink and Echo"
      )
  ).length,

articleInkAndEchoStories:
  allPosts.filter(
    p =>
      p.category === "Articles" &&
      p.subcategory === "Ink and Echo" &&
      p.subSubcategory === "Stories"
  ).length,

articleInkAndEchoPoems:
  allPosts.filter(
    p =>
      p.category === "Articles" &&
      p.subcategory === "Ink and Echo" &&
      p.subSubcategory === "Poems"
  ).length,

articleInkAndEchoArticles:
  allPosts.filter(
    p =>
      p.category === "Articles" &&
      p.subcategory === "Ink and Echo" &&
      p.subSubcategory === "Articles"
  ).length,

    news:
      allPosts.filter(
        p => p.category === "News"
      ).length,

    newsState:
      allPosts.filter(
        p =>
          p.category === "News" &&
          p.subcategory === "State"
      ).length,

    newsNational:
      allPosts.filter(
        p =>
          p.category === "News" &&
          p.subcategory === "National"
      ).length,

    newsOrganisational:
      allPosts.filter(
        p =>
          p.category === "News" &&
          p.subcategory === "Organisational"
      ).length,

    newsPressRelease:
      allPosts.filter(
        p =>
          p.category === "News" &&
          p.subcategory === "Press Release"
      ).length,

    campus:
      allPosts.filter(
        p => p.category === "Campus Report"
      ).length,

    photosVideos:
      allPosts.filter(
        p => isMediaCategory(p.category)
      ).length,

    photos:
      allPosts.filter(
        p =>
          isMediaCategory(p.category) &&
          p.subcategory === "Photos"
      ).length,

    videos:
      allPosts.filter(
        p =>
          isMediaCategory(p.category) &&
          p.subcategory === "Videos"
      ).length,

      chatraSanglap:
  allPosts.filter(
    p =>
      isMediaCategory(p.category) &&
      p.subcategory === "Chatra Sanglap"
  ).length,

    publications:
      publications.length,

    books:
      publications.filter(
        p => p.type === "book"
      ).length,

    newspapers:
      publications.filter(
        p => p.type === "newspaper"
      ).length,

    magazines:
      publications.filter(
        p => p.type === "magazine"
      ).length

  };

  console.log("All Posts:", allPosts.length);
  console.log("Filtered Posts:", filteredPosts.length);
  console.log("Current Posts:", currentPosts.length);

  const communityMembersRangeStart =
  communityMembers.length === 0
    ? 0
    : (
        communityMembersPage - 1
      ) *
        communityMembersPerPage +
      1;


const communityMembersRangeEnd =
  communityMembers.length === 0
    ? 0
    : communityMembersRangeStart +
      communityMembers.length -
      1;

  return (
  <div className="admin-page">

    <div className="admin-page-header">

      <div className="admin-page-header-text">

        <span className="admin-page-eyebrow">
          Content Management
        </span>

        <h1>
          Admin Upload Panel
        </h1>

        <p>
          Manage posts, publications,
          site content and settings.
        </p>

      </div>

      <button
        type="button"
        className="admin-logout-button"
        onClick={handleLogout}
      >
        Logout
      </button>

    </div>

<div className="admin-section-heading admin-dashboard-heading">

  <span className="admin-section-eyebrow">
    Content Analytics
  </span>

  <h2>
    Dashboard Overview
  </h2>

  <p>
    A quick overview of published content
    across Chatra Sangbad.
  </p>

</div>

      <div className="admin-dashboard">

        <div className="dashboard-card">
          <h3>Total Posts</h3>
          <p>{dashboardStats.total}</p>
        </div>

        <div className="dashboard-card">
          <h3>Featured</h3>
          <p>{dashboardStats.featured}</p>
        </div>

        <div className="dashboard-card">
          <h3>Editorial</h3>
          <p>{dashboardStats.editorial}</p>
        </div>

        <ExpandableDashboardCard
          title="Articles"
          total={dashboardStats.articles}
          cardKey="articles"
          expandedCard={expandedCard}
          setExpandedCard={setExpandedCard}
        >
          <div
            style={{
              display: "flex",
              justifyContent: "space-between"
            }}
          >
            <span>International</span>
            <span>{dashboardStats.articleInternational}</span>
          </div>

          <div
            style={{
              display: "flex",
              justifyContent: "space-between"
            }}
          >
            <span>National</span>
            <span>{dashboardStats.articleNational}</span>
          </div>

          <div
            style={{
              display: "flex",
              justifyContent: "space-between"
            }}
          >
            <span>State</span>
            <span>{dashboardStats.articleState}</span>
          </div>

          <div
            style={{
              display: "flex",
              justifyContent: "space-between"
            }}
          >
            <span>Sports</span>
            <span>{dashboardStats.articleSports}</span>
          </div>

          <div
            style={{
              display: "flex",
              justifyContent: "space-between"
            }}
          >
            <span>Science & Technology</span>
            <span>{dashboardStats.articleScience}</span>
          </div>

          <div
  style={{
    display: "flex",
    justifyContent: "space-between"
  }}
>
  <span>Ink and Echo</span>
  <span>{dashboardStats.articleInkAndEcho}</span>
</div>

<div className="dashboard-subgroup">
  <div
    style={{
      display: "flex",
      justifyContent: "space-between"
    }}
  >
    <span>↳ Stories</span>
    <span>{dashboardStats.articleInkAndEchoStories}</span>
  </div>

  <div
    style={{
      display: "flex",
      justifyContent: "space-between"
    }}
  >
    <span>↳ Poems</span>
    <span>{dashboardStats.articleInkAndEchoPoems}</span>
  </div>

  <div
    style={{
      display: "flex",
      justifyContent: "space-between"
    }}
  >
    <span>↳ Articles</span>
    <span>{dashboardStats.articleInkAndEchoArticles}</span>
  </div>
</div>

          <div
            style={{
              display: "flex",
              justifyContent: "space-between"
            }}
          >
            <span>Environment</span>
            <span>{dashboardStats.articleEnvironment}</span>
          </div>

          <div
            style={{
              display: "flex",
              justifyContent: "space-between"
            }}
          >
            <span>Her Space</span>
            <span>{dashboardStats.articleHerSpace}</span>
          </div>

          <div
            style={{
              display: "flex",
              justifyContent: "space-between"
            }}
          >
            <span>Tribal Voices</span>
            <span>{dashboardStats.articleTribalVoices}</span>
          </div>
        </ExpandableDashboardCard>

        <div className="dashboard-card">
          <h3>Campus Report</h3>
          <p>{dashboardStats.campus}</p>
        </div>

        <ExpandableDashboardCard
          title="News"
          total={dashboardStats.news}
          cardKey="news"
          expandedCard={expandedCard}
          setExpandedCard={setExpandedCard}
        >
          <div
            style={{
              display: "flex",
              justifyContent: "space-between"
            }}
          >
            <span>State</span>
            <span>{dashboardStats.newsState}</span>
          </div>

          <div
            style={{
              display: "flex",
              justifyContent: "space-between"
            }}
          >
            <span>National</span>
            <span>{dashboardStats.newsNational}</span>
          </div>

          <div
            style={{
              display: "flex",
              justifyContent: "space-between"
            }}
          >
            <span>Organisational</span>
            <span>{dashboardStats.newsOrganisational}</span>
          </div>

          <div
            style={{
              display: "flex",
              justifyContent: "space-between"
            }}
          >
            <span>Press Release</span>
            <span>{dashboardStats.newsPressRelease}</span>
          </div>
        </ExpandableDashboardCard>

        <ExpandableDashboardCard
          title="Media"
          total={dashboardStats.photosVideos}
          cardKey="photosVideos"
          expandedCard={expandedCard}
          setExpandedCard={setExpandedCard}
        >
          <div
            style={{
              display: "flex",
              justifyContent: "space-between"
            }}
          >
            <span>Photos</span>
            <span>{dashboardStats.photos}</span>
          </div>

          <div
            style={{
              display: "flex",
              justifyContent: "space-between"
            }}
          >
            <span>Videos</span>
            <span>{dashboardStats.videos}</span>
          </div>

          <div
  style={{
    display: "flex",
    justifyContent: "space-between"
  }}
>
  <span>Chatra Sanglap</span>
  <span>{dashboardStats.chatraSanglap}</span>
</div>
        </ExpandableDashboardCard>

        <ExpandableDashboardCard
          title="Publications"
          total={dashboardStats.publications}
          cardKey="publications"
          expandedCard={expandedCard}
          setExpandedCard={setExpandedCard}
        >
          <div
            style={{
              display: "flex",
              justifyContent: "space-between"
            }}
          >
            <span>Books</span>
            <span>{dashboardStats.books}</span>
          </div>

          <div
            style={{
              display: "flex",
              justifyContent: "space-between"
            }}
          >
            <span>Newspapers</span>
            <span>{dashboardStats.newspapers}</span>
          </div>

          <div
            style={{
              display: "flex",
              justifyContent: "space-between"
            }}
          >
            <span>Magazines</span>
            <span>{dashboardStats.magazines}</span>
          </div>
        </ExpandableDashboardCard>

            </div>


<section
  className="admin-management-hub"
  aria-labelledby="admin-management-hub-title"
>

  <div className="admin-section-heading admin-management-hub-heading">

    <span className="admin-section-eyebrow">
      Administrative Workspace
    </span>

    <h2 id="admin-management-hub-title">
      Management Hub
    </h2>

    <p>
      Select an administrative area to continue.
    </p>

  </div>


  <div className="admin-module-list">

  <div className="admin-module-group">

    <button
      type="button"
      className={`
        admin-module-card
        ${
          openAdminSections.postUpload
            ? "admin-module-card-open"
            : ""
        }
      `}
      onClick={() =>
        toggleAdminSection(
          "postUpload"
        )
      }
      aria-expanded={
        openAdminSections.postUpload
      }
      aria-controls="admin-section-postUpload"
    >

      <span className="admin-module-card-eyebrow">
        Content Creation
      </span>

      <strong className="admin-module-card-title">
        Post Upload
      </strong>

      <span className="admin-module-card-description">
        Create and publish articles, news, editorials and media content.
      </span>

    </button>

    <section
      id="admin-section-postUpload"
      className={`
        admin-module-panel
        ${
          openAdminSections.postUpload
            ? "admin-module-panel-open"
            : "admin-module-panel-collapsed"
        }
      `}
      aria-hidden={
        !openAdminSections.postUpload
      }
    >

      <div className="admin-module-panel-actions">

        <button
          type="button"
          className="
            admin-button
            admin-button-small
            admin-button-secondary
            admin-module-minimize
          "
          onClick={() =>
            minimizeAdminSection(
              "postUpload"
            )
          }
          aria-label="Minimize Post Upload"
        >
          Minimize
        </button>

      </div>

<div className="admin-section-heading">

  <span className="admin-section-eyebrow">
    Content Publishing
  </span>

  <h2>
    Post Upload
  </h2>

  <p>
    Create new posts or edit existing
    content from one workspace.
  </p>

</div>

<form
  className="admin-form-card"
  onSubmit={handleSubmit}
>
  <input
  type="text"
  name="title"
  placeholder="Post Title"
  value={formData.title}
  onChange={handleChange}
  required
/>

        <br />

        {!isPdfCategory &&
          !isMediaCategory(formData.category) && (

        <PublicationEditor
          value={formData.content}
          onChange={(content) =>
            setFormData({
              ...formData,
              content,
            })
          }
        />

      )}

      <br />

        <AdminSelect
  name="category"
  value={formData.category}
  onChange={handleChange}
  placeholder="Select Category"
  required
  options={
    Object.entries(
      CONTENT_TAXONOMY
    ).map(
      ([
        categoryKey,
        categoryConfig
      ]) => ({
        value:
          categoryKey,

        label:
          categoryConfig.label
      })
    )
  }
/>
       <br />

      <label>
        Publication Date
      </label>



      <div
  className="admin-date-row"
  style={{
    display: "flex",
    gap: "10px",
    alignItems: "center",
    flexWrap: "wrap",
  }}
>
        {(() => {
          const [selectedDay, selectedMonth, selectedYear] =
            formData.publishDate.split("/");

          const today = new Date();
          const currentYear = today.getFullYear();
          const currentMonth = today.getMonth() + 1;
          const currentDay = today.getDate();

          const yearNumber = Number(selectedYear);
          const monthNumber = Number(selectedMonth);

          // Number of days in selected month
          let maxDays = new Date(
            yearNumber,
            monthNumber,
            0
          ).getDate();

          // If selected year/month is the current month,
          // don't allow days beyond today.
          if (
            yearNumber === currentYear &&
            monthNumber === currentMonth
          ) {
            maxDays = currentDay;
          }

          // If selected date somehow becomes invalid,
          // automatically clamp it to the last valid day.
          const validDay = Math.min(
            Number(selectedDay),
            maxDays
          );

          if (validDay !== Number(selectedDay)) {
            setTimeout(() => {
              setFormData((prev) => ({
                ...prev,
                publishDate:
                  `${String(validDay).padStart(2, "0")}/` +
                  `${String(monthNumber).padStart(2, "0")}/` +
                  `${selectedYear}`,
              }));
            }, 0);
          }

          // -------------------------
          // YEARS
          // -------------------------

          const years = Array.from(
            {
              length: currentYear - 2020 + 1,
            },
            (_, i) => currentYear - i
          );

          // -------------------------
          // MONTHS
          // -------------------------

          const months = [
            ["01", "January"],
            ["02", "February"],
            ["03", "March"],
            ["04", "April"],
            ["05", "May"],
            ["06", "June"],
            ["07", "July"],
            ["08", "August"],
            ["09", "September"],
            ["10", "October"],
            ["11", "November"],
            ["12", "December"],
          ].filter(([value]) => {
            // If selected year is current year,
            // don't allow months after the current month.
            if (yearNumber === currentYear) {
              return Number(value) <= currentMonth;
            }

            return true;
          });

          return (
            <>
              {/* DAY */}

              <AdminSelect
                value={
                  String(
                    validDay
                  ).padStart(
                    2,
                    "0"
                  )
                }
                ariaLabel="Publication day"
                required
                options={
                  Array.from(
                    {
                      length:
                        maxDays
                    },
                    (_, i) => {

                      const day =
                        String(
                          i + 1
                        ).padStart(
                          2,
                          "0"
                        );

                      return {
                        value: day,
                        label: day
                      };

                    }
                  )
                }
                onChange={(e) => {

                  setFormData(
                    prev => ({
                      ...prev,

                      publishDate:
                        `${e.target.value}/` +
                        `${String(
                          monthNumber
                        ).padStart(
                          2,
                          "0"
                        )}/` +
                        `${selectedYear}`
                    })
                  );

                }}
              />

              /

              {/* MONTH */}

              <AdminSelect
                value={
                  String(
                    monthNumber
                  ).padStart(
                    2,
                    "0"
                  )
                }
                ariaLabel="Publication month"
                required
                options={
                  months.map(
                    ([value, label]) => ({
                      value,
                      label
                    })
                  )
                }
                onChange={(e) => {

                  const newMonth =
                    Number(
                      e.target.value
                    );


                  const newMaxDays =
                    new Date(
                      yearNumber,
                      newMonth,
                      0
                    ).getDate();


                  let newDay =
                    Math.min(
                      Number(
                        selectedDay
                      ),
                      newMaxDays
                    );


                  if (
                    yearNumber ===
                      currentYear &&
                    newMonth ===
                      currentMonth
                  ) {

                    newDay =
                      Math.min(
                        newDay,
                        currentDay
                      );

                  }


                  setFormData(
                    prev => ({
                      ...prev,

                      publishDate:
                        `${String(
                          newDay
                        ).padStart(
                          2,
                          "0"
                        )}/` +
                        `${String(
                          newMonth
                        ).padStart(
                          2,
                          "0"
                        )}/` +
                        `${selectedYear}`
                    })
                  );

                }}
              />

              /

              {/* YEAR */}

              <AdminSelect
                value={selectedYear}
                ariaLabel="Publication year"
                required
                options={
                  years.map(
                    year => ({
                      value:
                        String(year),

                      label:
                        String(year)
                    })
                  )
                }
                onChange={(e) => {

                  const newYear =
                    Number(
                      e.target.value
                    );


                  let newMonth =
                    monthNumber;


                  if (
                    newYear ===
                      currentYear &&
                    newMonth >
                      currentMonth
                  ) {

                    newMonth =
                      currentMonth;

                  }


                  const newMaxDays =
                    new Date(
                      newYear,
                      newMonth,
                      0
                    ).getDate();


                  let newDay =
                    Math.min(
                      Number(
                        selectedDay
                      ),
                      newMaxDays
                    );


                  if (
                    newYear ===
                      currentYear &&
                    newMonth ===
                      currentMonth
                  ) {

                    newDay =
                      Math.min(
                        newDay,
                        currentDay
                      );

                  }


                  setFormData(
                    prev => ({
                      ...prev,

                      publishDate:
                        `${String(
                          newDay
                        ).padStart(
                          2,
                          "0"
                        )}/` +
                        `${String(
                          newMonth
                        ).padStart(
                          2,
                          "0"
                        )}/` +
                        `${newYear}`
                    })
                  );

                }}
              />
            </>
          );
        })()}
      </div>

        {Object.keys(
  getTaxonomySubcategories(formData.category)
).length > 0 && (
<>
  <>
    <AdminSelect
      name="subcategory"
      value={
        formData.subcategory
      }
      onChange={
        handleChange
      }
      placeholder="Select Subcategory"
      required
      className="admin-custom-select-spaced"
      options={
        Object.entries(
          getTaxonomySubcategories(
            formData.category
          )
        ).map(
          ([
            subcategoryKey,
            subcategoryConfig
          ]) => ({
            value:
              subcategoryKey,

            label:
              subcategoryConfig.label
          })
        )
      }
    />

    {Object.keys(
  getTaxonomySubSubcategories(
    formData.category,
    formData.subcategory
  )
).length > 0 && (
  <AdminSelect
    name="subSubcategory"
    value={
      formData.subSubcategory
    }
    onChange={
      handleChange
    }
    placeholder="Select Sub-subcategory"
    required
    className="admin-custom-select-spaced"
    options={
      Object.entries(
        getTaxonomySubSubcategories(
          formData.category,
          formData.subcategory
        )
      ).map(
        ([
          subSubcategoryKey,
          subSubcategoryConfig
        ]) => ({
          value:
            subSubcategoryKey,

          label:
            subSubcategoryConfig.label
        })
      )
    }
  />
)}
  </>

          {isPhoto && (
  <>
  <br /><br />
              <label
                style={{
                  display: "block",
                  marginBottom: "8px",
                  fontWeight: "bold"
                }}
              >
                Photo Description
              </label>

              <div style={{ width: "90%" }}>
                <FooterDescriptionEditor
                  value={formData.content}
                  onChange={(content) =>
                    setFormData((currentFormData) => ({
                      ...currentFormData,
                      content
                    }))
                  }
                />
              </div>



<br /><br />

<label
  style={{
    display: "block",
    marginBottom: "8px",
    fontWeight: "bold"
  }}
>
  Photos
</label>


<input
  ref={photoInputRef}
  type="file"
  accept="image/*"
  multiple
  onChange={
    handlePhotoFilesSelect
  }
  style={{
    display: "none"
  }}
/>


<button
  type="button"
  className="
    admin-button
    admin-button-small
    admin-button-soft
  "
  onClick={() =>
    photoInputRef.current?.click()
  }
  disabled={
    photoItems.length >= 10
  }
>
  Add Photos
</button>


<p
  style={{
    marginTop: "7px",
    marginBottom: "0",
    fontSize: "13px",
    color: "#666"
  }}
>
  {photoItems.length} of 10{" "}
{photoItems.length === 1
  ? "photo"
  : "photos"}{" "}
in gallery • Maximum 3 MB per new image
</p>


{photoItems.length > 0 && (

  <>

    <p
      style={{
        marginTop: "8px",
        marginBottom: "10px",
        fontSize: "13px",
        color: "#666"
      }}
    >
      Drag photos to change their order,
      or use the arrow buttons.
      The number shows the gallery order.
    </p>


    <div className="admin-photo-grid">

      {photoItems.map(
        (
          photoItem,
          index
        ) => {

          const isCover =
            photoCoverId ===
            photoItem.id;


          const previewSource =
            photoItem.source ===
            "new"
              ? photoItem.previewUrl
              : getMediaUrl(
                  photoItem.url
                );


          return (

            <div
  key={photoItem.id}
  draggable
  onDragStart={() =>
    handlePhotoDragStart(
      photoItem.id
    )
  }
  onDragOver={
    event =>
      event.preventDefault()
  }
  onDrop={() =>
    handlePhotoDrop(
      photoItem.id
    )
  }
  onDragEnd={() =>
    setDraggedPhotoId(
      null
    )
  }
  className={`
    admin-photo-card
    ${
      isCover
        ? "admin-photo-card-cover"
        : ""
    }
    ${
      draggedPhotoId === photoItem.id
        ? "admin-photo-card-dragging"
        : ""
    }
  `}
>

              <div className="admin-photo-order">
  {index + 1}
</div>


              <img
  src={previewSource}
  alt={
    `Selected photo ${
      index + 1
    }`
  }
  draggable={false}
  className="admin-photo-preview"
/>


              <div className="admin-photo-cover-action">

                {isCover ? (

                  <strong className="admin-photo-cover-badge">
  ★ Cover
</strong>

                ) : (

                  <button
  type="button"
  className="
    admin-button
    admin-button-small
    admin-button-soft
  "
  onClick={() =>
    setPhotoCoverId(
      photoItem.id
    )
  }
>
  Set as Cover
</button>

                )}

              </div>


              <div className="admin-photo-controls">

                <button
                  type="button"
                  title="Move earlier"
                  aria-label={
                    `Move photo ${
                      index + 1
                    } earlier`
                  }
                  onClick={() =>
                    movePhotoItem(
                      photoItem.id,
                      "left"
                    )
                  }
                  disabled={
                    index === 0
                  }
                  className="
  admin-button
  admin-button-icon
"
                >
                  ←
                </button>


                <button
                  type="button"
                  title="Move later"
                  aria-label={
                    `Move photo ${
                      index + 1
                    } later`
                  }
                  onClick={() =>
                    movePhotoItem(
                      photoItem.id,
                      "right"
                    )
                  }
                  disabled={
                    index ===
                    photoItems.length -
                      1
                  }
                  className="
  admin-button
  admin-button-icon
"
                >
                  →
                </button>


                <button
  type="button"
  className="
    admin-button
    admin-button-small
    admin-button-danger
  "
  onClick={() =>
    removePhotoItem(
      photoItem.id
    )
  }
>
  Remove
</button>

              </div>

            </div>

          );

        }
      )}

    </div>

  </>

)}


            </>
          )}

          {isVideo && (
            <>
              <br /><br />

              <label
                style={{
                  display: "block",
                  marginBottom: "8px",
                  fontWeight: "bold"
                }}
              >
                Video Description
              </label>

              <div style={{ width: "90%" }}>
                <FooterDescriptionEditor
                  value={formData.content}
                  onChange={(content) =>
                    setFormData((currentFormData) => ({
                      ...currentFormData,
                      content
                    }))
                  }
                />
              </div>

              <br /><br />

              <input
                type="text"
                name="video"
                placeholder="Video URL"
                value={formData.video}
                onChange={handleChange}
                style={{
                  width: "90%",
                  padding: "10px"
                }}
              />


            </>
          )}

          <br />

          {formData.category === "Articles" && (
            <>
              <input
                type="text"
                name="writer"
                placeholder="Writer Name"
                value={formData.writer}
                onChange={handleChange}
                required
                style={{
                  display: "block",
                  width: "400px",
                  padding: "10px",
                  marginTop: "15px"
                }}
              />

              <br />
            </>
          )}

        </>

      )}

      {showCoverImage && (
        <>
          <br />

          <label
            style={{
              display: "block",
              marginBottom: "1px",
              fontWeight: "bold"
            }}
          >
            {isVideo
              ? "Video Thumbnail (Optional)"
              : "Cover Image (Optional)"}
          </label>

          <br />

          <input
  ref={coverImageInputRef}
  type="file"
  name="coverImage"
  accept="image/*"
  onChange={handleCoverImageSelect}
/>

<p
  style={{
    marginTop: "6px",
    marginBottom: "0",
    fontSize: "13px",
    color: "#666"
  }}
>
  Maximum image size: 3 MB
</p>

          {editId &&
            typeof formData.coverImage === "string" &&
            formData.coverImage && (
              <div style={{ marginTop: "12px" }}>
                <p style={{ marginBottom: "8px" }}>
                  Current {isVideo ? "thumbnail" : "cover image"} is attached.
                </p>

                <button
                  type="button"
                  onClick={() => {
                    const confirmed =
                      window.confirm(
                        `Remove the current ${
                          isVideo
                            ? "video thumbnail"
                            : "cover image"
                        }?`
                      );

                    if (!confirmed) return;

                    setFormData({
                      ...formData,
                      coverImage: ""
                    });

                    if (coverImageInputRef.current) {
                      coverImageInputRef.current.value = "";
                    }
                  }}
                  className="
  admin-button
  admin-button-small
  admin-button-danger
"
                >
                  Remove Current{" "}
                  {isVideo ? "Thumbnail" : "Cover Image"}
                </button>
              </div>
            )}


        </>
      )}

        {isPdfCategory && (

        <>
          <br />

          <input
            type="file"
            accept=".pdf"
            onChange={(e) =>
              setFormData({
                ...formData,
                pdf: e.target.files[0]
              })
            }
          />

          <br /><br />

        </>

      )}

      <br />

        <label className="admin-featured-control">

  <span>
    Featured Post
  </span>

  <input
    type="checkbox"
    name="featured"
    checked={formData.featured}
    onChange={handleChange}
  />

</label>

        <br /><br />

        <div className="admin-form-actions">

  <button
    type="submit"
    className="admin-button admin-button-primary"
  >
    {editId
      ? "Save Changes"
      : "Publish Post"}
  </button>

  <button
    type="button"
    className="admin-button admin-button-secondary"
    onClick={handleCancelEdit}
  >
    Cancel Edit
  </button>

</div>
      </form>

    </section>

  </div>

  <div className="admin-module-group">

    <button
      type="button"
      className={`
        admin-module-card
        ${
          openAdminSections.publicationUpload
            ? "admin-module-card-open"
            : ""
        }
      `}
      onClick={() =>
        toggleAdminSection(
          "publicationUpload"
        )
      }
      aria-expanded={
        openAdminSections.publicationUpload
      }
      aria-controls="admin-section-publicationUpload"
    >

      <span className="admin-module-card-eyebrow">
        Publication Creation
      </span>

      <strong className="admin-module-card-title">
        Publication Upload
      </strong>

      <span className="admin-module-card-description">
        Upload books, newspapers and magazines to the publication archive.
      </span>

    </button>

    <section
      id="admin-section-publicationUpload"
      className={`
        admin-module-panel
        ${
          openAdminSections.publicationUpload
            ? "admin-module-panel-open"
            : "admin-module-panel-collapsed"
        }
      `}
      aria-hidden={
        !openAdminSections.publicationUpload
      }
    >

      <div className="admin-module-panel-actions">

        <button
          type="button"
          className="
            admin-button
            admin-button-small
            admin-button-secondary
            admin-module-minimize
          "
          onClick={() =>
            minimizeAdminSection(
              "publicationUpload"
            )
          }
          aria-label="Minimize Publication Upload"
        >
          Minimize
        </button>

      </div>

<div className="admin-section-heading">

  <span className="admin-section-eyebrow">
    Digital Library
  </span>

  <h2>
    Publication Upload
  </h2>

  <p>
    Upload books, newspapers and magazines
    to the publication archive.
  </p>

</div>

<form
  className="admin-form-card admin-publication-form"
  onSubmit={handlePublicationSubmit}
>
        <div className="admin-publication-field">

  <label>
    Publication Type
  </label>
        <AdminSelect
          value={publicationData.type}
          placeholder=""
          ariaLabel="Publication type"
          options={[
            {
              value: "book",
              label: "Book"
            },
            {
              value: "newspaper",
              label: "Newspaper"
            },
            {
              value: "magazine",
              label: "Magazine"
            }
          ]}
          onChange={(e) => {

            setPublicationData({
              ...publicationData,
              type: e.target.value
            });

            setPublicationPdf(null);
            setPublicationCoverImage(null);

            if (
              publicationPdfInputRef.current
            ) {
              publicationPdfInputRef.current.value =
                "";
            }

            if (
              publicationCoverImageInputRef.current
            ) {
              publicationCoverImageInputRef.current.value =
                "";
            }

          }}
        />
        </div>

        {publicationData.type === "book" && (
          <>
            <div className="admin-publication-field">

  <input
    type="text"
    placeholder="Book Title"
    value={publicationData.title}
    onChange={(e) =>
      setPublicationData({
        ...publicationData,
        title: e.target.value,
      })
    }
    required
  />

</div>

<div className="admin-publication-field">

  <input
    type="text"
    placeholder="Author Name"
    value={publicationData.author}
    onChange={(e) =>
      setPublicationData({
        ...publicationData,
        author: e.target.value,
      })
    }
    required
  />

</div>
          </>
        )}

        {publicationData.type === "newspaper" && (
          <>
            <div className="admin-publication-field">

  <label>
    Month
  </label>
            <br />
            <AdminSelect
              value={publicationData.month}
              placeholder=""
              ariaLabel="Publication month"
              options={
                months.map(
                  month => ({
                    value: month,
                    label: month
                  })
                )
              }
              onChange={(e) =>
                setPublicationData({
                  ...publicationData,
                  month: e.target.value
                })
              }
            />
            </div>

            <div className="admin-publication-field">

  <label>
    Year
  </label>
            <br />
            <AdminSelect
              value={publicationData.year}
              placeholder=""
              ariaLabel="Publication year"
              options={
                years.map(
                  year => ({
                    value:
                      String(year),

                    label:
                      String(year)
                  })
                )
              }
              onChange={(e) =>
                setPublicationData({
                  ...publicationData,
                  year:
                    Number(
                      e.target.value
                    )
                })
              }
            />
            </div>

            <br /><br />

            <div className="admin-publication-field">

  <div className="admin-generated-title">

  <span className="admin-generated-title-label">
    Generated Title
  </span>

  <strong>
    ছাত্র সংবাদ {publicationData.month}{" "}
    {publicationData.year}
  </strong>

</div>
</div>
          </>
        )}

        {publicationData.type === "magazine" && (
          <>
            <div className="admin-publication-field">

  <label>
    Year
  </label>
            <br />
            <AdminSelect
              value={publicationData.year}
              placeholder=""
              ariaLabel="Publication year"
              options={
                years.map(
                  year => ({
                    value:
                      String(year),

                    label:
                      String(year)
                  })
                )
              }
              onChange={(e) =>
                setPublicationData({
                  ...publicationData,
                  year:
                    Number(
                      e.target.value
                    )
                })
              }
            />
            </div>

            <br /><br />

            <div className="admin-publication-field">

  <div className="admin-generated-title">

  <span className="admin-generated-title-label">
    Generated Title
  </span>

  <strong>
    ছাত্র সংবাদ Conference Edition{" "}
    {publicationData.year}
  </strong>

</div>
</div>
          </>
        )}

        <div className="admin-publication-field">

  <label>
    Upload PDF{" "}
    <span className="admin-field-note">
      (Max 10 MB)
    </span>
  </label>

  <input
    ref={publicationPdfInputRef}
    type="file"
    accept="application/pdf"
    onChange={(e) => {

      const selectedFile =
        e.target.files[0];

      if (
        selectedFile &&
        selectedFile.size >
          10 * 1024 * 1024
      ) {

        alert(
          "PDF must be 10 MB or smaller."
        );

        e.target.value = "";

        setPublicationPdf(null);

        return;
      }

      setPublicationPdf(
        selectedFile || null
      );

    }}
    required
  />

</div>

        <br /><br />
<div className="admin-publication-field">

  <label>
    Cover Image (Optional)
  </label>

  <input
    ref={publicationCoverImageInputRef}
    type="file"
    accept="image/*"
    onChange={handlePublicationCoverSelect}
  />

  <p className="admin-field-help">
    Maximum image size: 3 MB
  </p>

</div>

        <br /><br />

        <div className="admin-form-actions">

  <button
    type="submit"
    className="admin-button admin-button-primary"
  >
    Upload Publication
  </button>

</div>

        </form>

    </section>

  </div>

  <div className="admin-module-group">

    <button
      type="button"
      className={`
        admin-module-card
        ${
          openAdminSections.managePosts
            ? "admin-module-card-open"
            : ""
        }
      `}
      onClick={() =>
        toggleAdminSection(
          "managePosts"
        )
      }
      aria-expanded={
        openAdminSections.managePosts
      }
      aria-controls="admin-section-managePosts"
    >

      <span className="admin-module-card-eyebrow">
        Content Management
      </span>

      <strong className="admin-module-card-title">
        Manage Posts
      </strong>

      <span className="admin-module-card-description">
        Search, edit, organize and manage published site content.
      </span>

    </button>

    <section
      id="admin-section-managePosts"
      className={`
        admin-module-panel
        ${
          openAdminSections.managePosts
            ? "admin-module-panel-open"
            : "admin-module-panel-collapsed"
        }
      `}
      aria-hidden={
        !openAdminSections.managePosts
      }
    >

      <div className="admin-module-panel-actions">

        <button
          type="button"
          className="
            admin-button
            admin-button-small
            admin-button-secondary
            admin-module-minimize
          "
          onClick={() =>
            minimizeAdminSection(
              "managePosts"
            )
          }
          aria-label="Minimize Manage Posts"
        >
          Minimize
        </button>

      </div>

<div className="admin-section-heading admin-management-heading">

  <span className="admin-section-eyebrow">
    Content Management
  </span>

  <h2>
    Manage Posts
  </h2>

  <p>
    Search, filter, sort and manage
    published content from one place.
  </p>

</div>

<div className="admin-post-toolbar">

      <input
  className="admin-post-search"
  type="text"
  placeholder="Search posts..."
  value={searchTerm}
  onChange={(e) => {

    setSearchTerm(
      e.target.value
    );

    setCurrentPage(1);

  }}
/>

      {searchTerm && (

  <button
    type="button"
    className="admin-search-clear"
    onClick={() => {
      setSearchTerm("");
      setCurrentPage(1);
    }}
    aria-label="Clear search"
  >
    ✕
  </button>

)}

      {" "}

      <AdminSelect
        value={selectedCategory}
        placeholder="All Categories"
        ariaLabel="Post category"
        className="admin-custom-select-filter"
        options={[
          {
            value: "Editorial",
            label: "Editorial"
          },
          {
            value: "Articles",
            label: "Articles"
          },
          {
            value: "News",
            label: "News"
          },
          {
            value: "Campus Report",
            label: "Campus Report"
          },
          {
            value: "Media",
            label: "Media"
          }
        ]}
        onChange={(e) => {

          setSelectedCategory(
            e.target.value
          );

          setCurrentPage(1);

        }}
      />

      <AdminSelect
        value={sortOption}
        placeholder=""
        ariaLabel="Sort posts"
        className="admin-custom-select-filter"
        options={[
          {
            value: "newest",
            label: "Newest First"
          },
          {
            value: "oldest",
            label: "Oldest First"
          },
          {
            value: "az",
            label: "Title A-Z"
          },
          {
            value: "za",
            label: "Title Z-A"
          },
          {
            value: "featured",
            label: "Featured First"
          }
        ]}
        onChange={(e) =>
          setSortOption(
            e.target.value
          )
        }
      />
      </div>

      <div className="admin-post-select-row">

        <label className="admin-select-visible">

          <input
            type="checkbox"

            checked={
              currentPosts.length > 0 &&
              currentPosts.every(post =>
                selectedPosts.includes(post._id)
              )
            }

            onChange={(e) => {

            const currentIds =
                currentPosts.map(post => post._id);

            if (e.target.checked) {

                setSelectedPosts(prev => [

                    ...prev,

                    ...currentIds.filter(
                        id => !prev.includes(id)
                    )

                ]);

            }

            else {

                setSelectedPosts(prev =>

                    prev.filter(
                        id => !currentIds.includes(id)
                    )

                );

            }

        }}

          />

          Select Visible

        </label>

      </div>

      <div className="admin-table-wrap">

  <table className="admin-data-table">

      <thead>
        <tr>
          <th>#</th>

<th className="admin-select-column">
  Select
</th>
          <th>Title</th>
          <th>Category</th>
          <th>Featured</th>
          <th>Published</th>
          <th>Actions</th>
        </tr>
      </thead>

      <tbody>

        {currentPosts.map((post, index) => (

          <tr key={post._id}>

            <td>
                {indexOfFirstPost + index + 1}
            </td>

            <td>
              <input
                type="checkbox"
                checked={selectedPosts.includes(post._id)}
                onChange={(e) => {

                if (e.target.checked) {

                    setSelectedPosts(prev => [

                        ...prev,

                        post._id

                    ]);

                }

                else {

                    setSelectedPosts(prev =>

                        prev.filter(
                            id => id !== post._id
                        )

                    );

                }

            }}
              />
            </td>

            <td title={post.title}>
              {
                post.title.length > 40
                  ? post.title.substring(0, 40) + "..."
                  : post.title
              }
            </td>

            <td>
  {getCategoryLabel(post.category)}

  {post.subcategory && (
    <>
      <br />
      <small>
        {getSubcategoryLabel(post.subcategory)}
      </small>
    </>
  )}

  {post.subSubcategory && (
    <>
      <br />
      <small>
        {post.subSubcategory}
      </small>
    </>
  )}
</td>

            <td>
              {post.featured ? "Yes" : "No"}
            </td>

            <td>
              {post.publishDate ||
                (
                  post.createdAt
                    ? new Date(
                        post.createdAt
                      ).toLocaleDateString("en-GB")
                    : "—"
                )}
            </td>

            <td>

              <div className="admin-table-actions">

  <button
    type="button"
    className="
      admin-button
      admin-button-small
      admin-button-secondary
    "
    onClick={() => handleEdit(post)}
  >
    Edit
  </button>

  <button
    type="button"
    className="
      admin-button
      admin-button-small
      admin-button-danger
    "
    onClick={() =>
      openDeletePopup([post._id])
    }
  >
    Delete
  </button>

</div>

            </td>

          </tr>

        ))}

      </tbody>

    </table>

    </div>

    <div className="admin-table-footer">

    <div className="admin-pagination">

      <button
        disabled={currentPage === 1}
        type="button"
className="
  admin-button
  admin-button-small
  admin-button-secondary
"
        onClick={() =>
          setCurrentPage(currentPage - 1)
        }
      >
        Previous
      </button>

      {" "}

      <span className="admin-page-indicator">
  Page {currentPage} of{" "}
  {Math.max(
    1,
    Math.ceil(
      sortedPosts.length / postsPerPage
    )
  )}
</span>

      {" "}

      <button
  disabled={
    currentPage >=
    Math.max(
      1,
      Math.ceil(
        sortedPosts.length /
        postsPerPage
      )
    )
  }
  type="button"
className="
  admin-button
  admin-button-small
  admin-button-secondary
"
  onClick={() =>
    setCurrentPage(
      Math.min(
        currentPage + 1,
        Math.max(
          1,
          Math.ceil(
            sortedPosts.length /
            postsPerPage
          )
        )
      )
    )
  }
>
  Next
</button>

    </div>

    <div className="admin-bulk-action">

        <button
        type="button"
className="
  admin-button
  admin-button-small
  admin-button-danger
"
disabled={selectedPosts.length === 0}

        onClick={() =>
            openDeletePopup(selectedPosts)
        }
    >

            Delete Selected ({selectedPosts.length})

        </button>

    </div>

</div>

    </section>

  </div>

  <div className="admin-module-group">

    <button
      type="button"
      className={`
        admin-module-card
        ${
          openAdminSections.managePublications
            ? "admin-module-card-open"
            : ""
        }
      `}
      onClick={() =>
        toggleAdminSection(
          "managePublications"
        )
      }
      aria-expanded={
        openAdminSections.managePublications
      }
      aria-controls="admin-section-managePublications"
    >

      <span className="admin-module-card-eyebrow">
        Publication Management
      </span>

      <strong className="admin-module-card-title">
        Manage Publications
      </strong>

      <span className="admin-module-card-description">
        Review and manage books, newspapers and magazines in the archive.
      </span>

    </button>

    <section
      id="admin-section-managePublications"
      className={`
        admin-module-panel
        ${
          openAdminSections.managePublications
            ? "admin-module-panel-open"
            : "admin-module-panel-collapsed"
        }
      `}
      aria-hidden={
        !openAdminSections.managePublications
      }
    >

      <div className="admin-module-panel-actions">

        <button
          type="button"
          className="
            admin-button
            admin-button-small
            admin-button-secondary
            admin-module-minimize
          "
          onClick={() =>
            minimizeAdminSection(
              "managePublications"
            )
          }
          aria-label="Minimize Manage Publications"
        >
          Minimize
        </button>

      </div>

<div className="admin-section-heading admin-management-heading">

  <span className="admin-section-eyebrow">
    Publication Management
  </span>

  <h2>
    Manage Publications
  </h2>

  <p>
    Filter, review and manage uploaded
    books, newspapers and magazines.
  </p>

</div>

      <div className="admin-management-toolbar">
          <label>
  Category
</label>

          <AdminSelect
              value={publicationCategory}
              placeholder="All Categories"
              ariaLabel="Publication category"
              className="admin-custom-select-filter"
              options={[
                {
                  value: "book",
                  label: "Books"
                },
                {
                  value: "newspaper",
                  label: "Newspapers"
                },
                {
                  value: "magazine",
                  label: "Magazines"
                }
              ]}
              onChange={(e) => {
                  setPublicationCategory(e.target.value);
              }}
          />
      </div>

      <label className="admin-select-visible">
          <input
              type="checkbox"
              checked={
                  currentPublications.length > 0 &&
                  currentPublications.every(
                      pub =>
                          selectedPublications.includes(
                              pub._id
                          )
                  )
              }
              onChange={(e) => {

                  if (e.target.checked) {

                      setSelectedPublications(prev => [
                          ...new Set([
                              ...prev,
                              ...currentPublications.map(
                                  pub => pub._id
                              )
                          ])
                      ]);

                  } else {

                      setSelectedPublications(prev =>
                          prev.filter(
                              id =>
                                  !currentPublications.some(
                                      pub =>
                                          pub._id === id
                                  )
                          )
                      );

                  }

              }}
          />

          <span>
  Select Visible
</span>

</label>

      {loadingPublications ? (
  <p>Loading publications...</p>
) : filteredPublications.length === 0 ? (
  <p>
    {publicationCategory
      ? "No publications found in this category."
      : "No publications uploaded yet."}
  </p>
) : (
    <>
      <div className="admin-table-wrap">

  <table className="admin-data-table">
          <thead>
            <tr>
              <th>#</th>
<th>Select</th>
              <th>Type</th>
              <th>Title</th>
              <th>Details</th>
              <th>PDF</th>
              <th>Action</th>
            </tr>
          </thead>

          <tbody>
            {currentPublications.map((pub, index) => (
              <tr key={pub._id}>

                <td>
                    {publicationIndexOfFirst + index + 1}
                </td>

                <td>
                    <input
                        type="checkbox"
                        checked={
                            selectedPublications.includes(pub._id)
                        }
                        onChange={() =>
                            handlePublicationSelection(pub._id)
                        }
                    />
                </td>

                <td>
  <span className="admin-type-badge">
    {pub.type}
  </span>
</td>
                <td>{pub.title}</td>
                <td>
                  {pub.type === "book" && (
                    pub.createdAt
                      ? new Date(
                          pub.createdAt
                        ).toLocaleDateString("en-GB")
                      : "—"
                  )}

                  {pub.type === "newspaper" &&
                    `${pub.month} ${pub.year}`}

                  {pub.type === "magazine" &&
                    `Conference ${pub.year}`}
                </td>
                <td>
                  <a
  className="admin-table-link"
  href={getMediaUrl(pub.pdf)}
  target="_blank"
  rel="noreferrer"
>
  View PDF
</a>
                </td>
                <td>
                  <button
  type="button"
  className="
    admin-button
    admin-button-small
    admin-button-danger
  "
  onClick={() =>
    handleDeletePublication(pub._id)
  }
>
  Delete
</button>
                </td>
              </tr>
            ))}
          </tbody>
            </table>
            </div>

          <div className="admin-table-footer">
          {/* LEFT — Pagination */}
          <div className="admin-pagination">

              <button
  type="button"
  className="
    admin-button
    admin-button-small
    admin-button-secondary
  "
  onClick={() =>
    setPublicationCurrentPage(
      prev => Math.max(prev - 1, 1)
    )
  }
  disabled={publicationCurrentPage === 1}
>
  Previous
</button>

              <span className="admin-page-indicator">
  Page {publicationCurrentPage} of{" "}
  {Math.max(publicationTotalPages, 1)}
</span>

              <button
  type="button"
  className="
    admin-button
    admin-button-small
    admin-button-secondary
  "
  onClick={() =>
                      setPublicationCurrentPage(
                          prev =>
                              Math.min(
                                  prev + 1,
                                  publicationTotalPages
                              )
                      )
                  }
                  disabled={
                      publicationCurrentPage >=
                      publicationTotalPages
                  }
>
  Next
</button>

          </div>

          {/* RIGHT — Delete Selected */}
          <button
  type="button"
  className="
    admin-button
    admin-button-small
    admin-button-danger
  "
  onClick={() => {

    if (selectedPublications.length === 0) {
      return;
    }

    setDeleteTarget(selectedPublications);

    setDeleteType("publication");

    setShowDeletePopup(true);

  }}
  disabled={
    selectedPublications.length === 0
  }
>
  Delete Selected ({selectedPublications.length})
</button>

      </div>
    </>
  )}

    </section>

  </div>

  <div className="admin-module-group">

    <button
      type="button"
      className={`
        admin-module-card
        ${
          openAdminSections.circleMembers
            ? "admin-module-card-open"
            : ""
        }
      `}
      onClick={() =>
        toggleAdminSection(
          "circleMembers"
        )
      }
      aria-expanded={
        openAdminSections.circleMembers
      }
      aria-controls="admin-section-circleMembers"
    >

      <span className="admin-module-card-eyebrow">
        Community
      </span>

      <strong className="admin-module-card-title">
        Circle Members
      </strong>

      <span className="admin-module-card-description">
        Review and manage registered members of the Chatra Sangbad Circle.
      </span>

    </button>

    <section
      id="admin-section-circleMembers"
      className={`
        admin-module-panel
        ${
          openAdminSections.circleMembers
            ? "admin-module-panel-open"
            : "admin-module-panel-collapsed"
        }
      `}
      aria-hidden={
        !openAdminSections.circleMembers
      }
    >

      <div className="admin-module-panel-actions">

        <button
          type="button"
          className="
            admin-button
            admin-button-small
            admin-button-secondary
            admin-module-minimize
          "
          onClick={() =>
            minimizeAdminSection(
              "circleMembers"
            )
          }
          aria-label="Minimize Circle Members"
        >
          Minimize
        </button>

      </div>

<div className="admin-section-heading admin-management-heading">

  <span className="admin-section-eyebrow">
    Community Management
  </span>

  <h2>
    Circle Members
  </h2>

  <p>
    Review, search and manage registered
    members of the Chatra Sangbad Circle.
  </p>

</div>

<section className="admin-community-section">

  <div
  style={{
    marginBottom: "18px"
  }}
>

{/* Member summary + Refresh */}

<div className="admin-community-header">

  <div className="admin-community-summary">

    <span className="admin-community-summary-label">
      Registered Members
    </span>

    <p>
      Total members:{" "}

      <strong>
        {communityMembersTotal}
      </strong>

      {communityMembersSearch && (
        <>
          {" "}
          matching{" "}

          <strong>
            "{communityMembersSearch}"
          </strong>
        </>
      )}
    </p>

  </div>


  <button
    type="button"
    className="
      admin-button
      admin-button-small
      admin-button-secondary
      admin-community-refresh
    "
    onClick={() =>
      fetchCommunityMembers({
        page:
          communityMembersPage,
        search:
          communityMembersSearch
      })
    }
    disabled={
      communityMembersLoading
    }
  >
    {communityMembersLoading
      ? "Loading..."
      : "Refresh"}
  </button>

</div>


  {/* Search controls */}
  <form
  className="admin-community-search"
  onSubmit={
    handleCommunityMembersSearch
  }
>

    <input

      className="admin-community-search-input"
      type="search"
      value={
        communityMembersSearchInput
      }
      onChange={(event) =>
        setCommunityMembersSearchInput(
          event.target.value
        )
      }
      placeholder="Search name, email, phone, school or college..."
      maxLength={100}
      disabled={
        communityMembersLoading
      }
      aria-label="Search Circle members"
    />


    <button
  type="submit"
  className="
    admin-button
    admin-button-small
    admin-button-primary
  "
  disabled={
    communityMembersLoading
  }
>
  Search
</button>


    <button
      type="button"
      className="
  admin-button
  admin-button-small
  admin-button-secondary
"
      onClick={
        clearCommunityMembersSearch
      }
      disabled={
        communityMembersLoading ||
        (
          !communityMembersSearchInput &&
          !communityMembersSearch
        )
      }

    >
      Clear
    </button>

  </form>

</div>

{!communityMembersLoading &&
  !communityMembersError &&
  communityMembers.length > 0 && (

  <p className="admin-community-result-status">
    Showing{" "}
    <strong>
      {communityMembersRangeStart}
    </strong>
    {"–"}
    <strong>
      {communityMembersRangeEnd}
    </strong>
    {" "}of{" "}
    <strong>
      {communityMembersTotal}
    </strong>
    {communityMembersSearch
      ? " matching members"
      : " members"}
  </p>

)}


  {communityMembersLoading ? (

    <div className="admin-community-state">
  Loading Circle members...
</div>

  ) : communityMembersError ? (

    <div className="admin-community-state admin-community-state-error">
  {communityMembersError}
</div>

  ) : communityMembers.length === 0 ? (

  <div className="admin-community-state admin-community-state-empty">

    {communityMembersSearch
      ? (
          <>
            No Circle members matched{" "}
            <strong>
              "{communityMembersSearch}"
            </strong>
            .
          </>
        )
      : "No Circle members registered yet."}

  </div>

) : (

    <div className="admin-community-list">

      {communityMembers.map(
  member => (

    <div
      key={member._id}
      className="admin-community-card"
    >

      <div className="admin-community-card-main">

        <div className="admin-community-member-content">

          <h3 className="admin-community-member-name">
            {member.name ||
              "Unnamed member"}
          </h3>


          <div className="admin-community-details">

            <div className="admin-community-detail">

              <strong className="admin-community-detail-label">
                Email
              </strong>

              <div className="admin-community-detail-value">
                {member.email}
              </div>

            </div>


            <div className="admin-community-detail">

              <strong className="admin-community-detail-label">
                Phone
              </strong>

              <div className="admin-community-detail-value">
                {member.phone || "—"}
              </div>

            </div>


            <div className="admin-community-detail">

              <strong className="admin-community-detail-label">
                School
              </strong>

              <div className="admin-community-detail-value">
                {member.school || "—"}
              </div>

            </div>


            <div className="admin-community-detail">

              <strong className="admin-community-detail-label">
                College / University
              </strong>

              <div className="admin-community-detail-value">
                {member.collegeUniversity || "—"}
              </div>

            </div>


            <div className="admin-community-detail">

              <strong className="admin-community-detail-label">
                Email verified
              </strong>

              <div className="admin-community-detail-value">
                {member.emailVerifiedAt
                  ? new Date(
                      member.emailVerifiedAt
                    ).toLocaleString()
                  : "—"}
              </div>

            </div>


            <div className="admin-community-detail">

              <strong className="admin-community-detail-label">
                Joined
              </strong>

              <div className="admin-community-detail-value">
                {member.createdAt
                  ? new Date(
                      member.createdAt
                    ).toLocaleString()
                  : "—"}
              </div>

            </div>

          </div>


          <div className="admin-community-opinion">

            <strong className="admin-community-opinion-label">
              Opinion / Feedback
            </strong>

            <div className="admin-community-opinion-box">
              {member.opinion ||
                "No opinion or feedback submitted."}
            </div>

          </div>

        </div>


        <button
          type="button"
          className="
            admin-button
            admin-button-small
            admin-button-danger
            admin-community-remove
          "
          onClick={() =>
            removeCommunityMember(
              member
            )
          }
          disabled={
            communityMemberDeletingId !== null
          }
        >
          {communityMemberDeletingId ===
          member._id
            ? "Removing..."
            : "Remove"}
        </button>

      </div>

    </div>

  )
)}


      {/* Circle-member pagination */}
      <div className="admin-community-pagination">
        <button
          type="button"
          className="
  admin-button
  admin-button-small
  admin-button-secondary
"
          onClick={() =>
            goToCommunityMembersPage(
              communityMembersPage - 1
            )
          }
          disabled={
            communityMembersLoading ||
            communityMembersPage <= 1
          }

        >
          Previous
        </button>


        <span className="admin-page-indicator">
  Page{" "}
  {communityMembersPage}
  {" "}of{" "}
  {Math.max(
    communityMembersTotalPages,
    1
  )}
</span>


        <button
          type="button"
          className="
  admin-button
  admin-button-small
  admin-button-secondary
"
          onClick={() =>
            goToCommunityMembersPage(
              communityMembersPage + 1
            )
          }
          disabled={
            communityMembersLoading ||
            communityMembersTotalPages === 0 ||
            communityMembersPage >=
              communityMembersTotalPages
          }

        >
          Next
        </button>

      </div>

    </div>

  )}

</section>

    </section>

  </div>

  <div className="admin-module-group">

    <button
      type="button"
      className={`
        admin-module-card
        ${
          openAdminSections.aboutUs
            ? "admin-module-card-open"
            : ""
        }
      `}
      onClick={() =>
        toggleAdminSection(
          "aboutUs"
        )
      }
      aria-expanded={
        openAdminSections.aboutUs
      }
      aria-controls="admin-section-aboutUs"
    >

      <span className="admin-module-card-eyebrow">
        Site Information
      </span>

      <strong className="admin-module-card-title">
        About Us Management
      </strong>

      <span className="admin-module-card-description">
        Manage About Us content, the editorial team and past editors.
      </span>

    </button>

    <section
      id="admin-section-aboutUs"
      className={`
        admin-module-panel
        ${
          openAdminSections.aboutUs
            ? "admin-module-panel-open"
            : "admin-module-panel-collapsed"
        }
      `}
      aria-hidden={
        !openAdminSections.aboutUs
      }
    >

      <div className="admin-module-panel-actions">

        <button
          type="button"
          className="
            admin-button
            admin-button-small
            admin-button-secondary
            admin-module-minimize
          "
          onClick={() =>
            minimizeAdminSection(
              "aboutUs"
            )
          }
          aria-label="Minimize About Us Management"
        >
          Minimize
        </button>

      </div>

<div className="admin-section-heading admin-management-heading">

  <span className="admin-section-eyebrow">
    Site Information
  </span>

  <h2>
    About Us Management
  </h2>

  <p>
    Manage the About Us content,
    editorial team and past editors.
  </p>

</div>

        {aboutUsLoading ? (

            <p>Loading About Us...</p>

        ) : (

            <div className="admin-about-section">

                {/* ========================================= */}
                {/* ABOUT US TEXT */}
                {/* ========================================= */}
                <div className="admin-about-subheading">

  <span>
    Page Content
  </span>

  <h3>
    About Us Content
  </h3>

</div>

                {editingAboutUs ? (

                    <>

                        <PublicationEditor
                            value={aboutUsDraft}
                            onChange={(content) =>
                                setAboutUsDraft(content)
                            }
                        />

                        <div className="admin-about-actions">

                            <button
  type="button"
  className="
    admin-button
    admin-button-primary
  "
  onClick={saveAboutUsContent}
>
  Save Changes
</button>


                            <button
  type="button"
  className="
    admin-button
    admin-button-secondary
  "
  onClick={cancelAboutUsEdit}
>
  Cancel
</button>


                            <button
  type="button"
  className="
    admin-button
    admin-button-danger
  "
  onClick={deleteAboutUsContent}
>
  Delete Content
</button>

                        </div>

                    </>

                ) : (

                    <>

                        <div
  className="admin-about-preview"
  dangerouslySetInnerHTML={{
    __html:
        aboutUs.content
            ? sanitizeRichHtml(
                aboutUs.content
              )
            : "<p>No About Us content available.</p>"
}}
                        />

                        <button
  type="button"
  className="
    admin-button
    admin-button-primary
    admin-about-edit-button
  "
  onClick={startEditingAboutUs}
>
  Edit About Us
</button>

                    </>

                )}

                {/* ========================================= */}
                {/* EDITORIAL TEAM */}
                {/* ========================================= */}

                <div className="admin-about-divider" />

<div className="admin-about-subheading">

  <span>
    Team Management
  </span>

  <h3>
    Editorial Team
  </h3>

</div>

                <div className="admin-about-add-row">

                    <input
                    className="admin-about-name-input"
                        type="text"
                        placeholder="Enter member name"
                        value={newEditorialMember}
                        onChange={(e) =>
                            setNewEditorialMember(
                                e.target.value
                            )
                        }

                    />

                    <button
  type="button"
  className="
    admin-button
    admin-button-small
    admin-button-primary
  "
  onClick={addEditorialMember}
>
  Add Member
</button>

                </div>
                <div className="admin-about-member-list">

                    {aboutUs.editorialTeam.map((member, index) => (

                        <div
  key={member._id}
  className="admin-about-member-row"
>

                            <strong className="admin-about-member-number">
  {index + 1}.
</strong>


                            {editingEditorialId ===
                            member._id ? (

                                <>

                                    <input
  className="admin-about-edit-input"
  value={
    editingEditorialName
  }
  onChange={(e) =>
    setEditingEditorialName(
      e.target.value
    )
  }
/>

                                    <button
  type="button"
  className="
    admin-button
    admin-button-small
    admin-button-primary
  "
  onClick={() =>
    saveEditorialMember(
      member._id
    )
  }
>
  Save
</button>

                                    <button
  type="button"
  className="
    admin-button
    admin-button-small
    admin-button-secondary
  "
  onClick={() => {
    setEditingEditorialId(
      null
    );

    setEditingEditorialName(
      ""
    );
  }}
>
  Cancel
</button>

                                </>

                            ) : (

                                <>

                                    <span className="admin-about-member-name">
  {member.name}
</span>

                                    <button
                                        type="button"
                                        className="
  admin-button
  admin-button-icon
"
                                        disabled={index === 0}
                                        onClick={() =>
                                            moveMember(
                                                "editorialTeam",
                                                index,
                                                "up"
                                            )
                                        }
                                    >
                                        ↑
                                    </button>

                                    <button
                                        type="button"
                                        className="
  admin-button
  admin-button-icon
"
                                        disabled={
                                            index ===
                                            aboutUs.editorialTeam.length - 1
                                        }
                                        onClick={() =>
                                            moveMember(
                                                "editorialTeam",
                                                index,
                                                "down"
                                            )
                                        }
                                    >
                                        ↓
                                    </button>

                                    <button
  type="button"
  className="
    admin-button
    admin-button-small
    admin-button-secondary
  "
  onClick={() => {

    setEditingEditorialId(
      member._id
    );

    setEditingEditorialName(
      member.name
    );

  }}
>
  Edit
</button>

                                    <button
  type="button"
  className="
    admin-button
    admin-button-small
    admin-button-danger
  "
  onClick={() =>
    deleteEditorialMember(
      member._id
    )
  }
>
  Delete
</button>

                                </>

                            )}

                        </div>

                    ))}

                </div>


                {/* ========================================= */}
                {/* PAST EDITORS */}
                {/* ========================================= */}

                <div className="admin-about-divider" />

<div className="admin-about-subheading">

  <span>
    Editorial History
  </span>

  <h3>
    Past Editors
  </h3>

</div>

                <div className="admin-about-add-row">

                    <input
  className="admin-about-name-input"
  type="text"
  placeholder="Enter past editor name"
  value={newPastEditor}
  onChange={(e) =>
    setNewPastEditor(
      e.target.value
    )
  }
/>

                    <button
  type="button"
  className="
    admin-button
    admin-button-small
    admin-button-primary
  "
  onClick={addPastEditor}
>
  Add Editor
</button>

                </div>


                <div className="admin-about-member-list">

                    {aboutUs.pastEditors.map((member, index) => (

                        <div
  key={member._id}
  className="admin-about-member-row"
>

                            <strong className="admin-about-member-number">
  {index + 1}.
</strong>


                            {editingPastEditorId ===
                            member._id ? (

                                <>

                                    <input
  className="admin-about-edit-input"
  value={
    editingPastEditorName
  }
  onChange={(e) =>
    setEditingPastEditorName(
      e.target.value
    )
  }
/>

                                    <button
  type="button"
  className="
    admin-button
    admin-button-small
    admin-button-primary
  "
  onClick={() =>
    savePastEditor(
      member._id
    )
  }
>
  Save
</button>

                                    <button
  type="button"
  className="
    admin-button
    admin-button-small
    admin-button-secondary
  "
  onClick={() => {

    setEditingPastEditorId(
      null
    );

    setEditingPastEditorName(
      ""
    );

  }}
>
  Cancel
</button>

                                </>

                            ) : (

                                <>

                                    <span className="admin-about-member-name">
  {member.name}
</span>

                                    <button
                                        type="button"
                                        className="
  admin-button
  admin-button-icon
"
                                        disabled={index === 0}
                                        onClick={() =>
                                            moveMember(
                                                "pastEditors",
                                                index,
                                                "up"
                                            )
                                        }
                                    >
                                        ↑
                                    </button>

                                    <button
                                        type="button"
                                        className="
  admin-button
  admin-button-icon
"
                                        disabled={
                                            index ===
                                            aboutUs.pastEditors.length - 1
                                        }
                                        onClick={() =>
                                            moveMember(
                                                "pastEditors",
                                                index,
                                                "down"
                                            )
                                        }
                                    >
                                        ↓
                                    </button>

                                    <button
                                        type="button"
                                        className="
  admin-button
  admin-button-small
  admin-button-secondary
"
                                        onClick={() => {

                                            setEditingPastEditorId(
                                                member._id
                                            );

                                            setEditingPastEditorName(
                                                member.name
                                            );

                                        }}
                                    >
                                        Edit
                                    </button>

                                    <button
  type="button"
  className="
    admin-button
    admin-button-small
    admin-button-danger
  "
  onClick={() =>
    deletePastEditor(
      member._id
    )
  }
>
  Delete
</button>

                                </>

                            )}

                        </div>

                    ))}

                </div>

            </div>

        )}

    </section>

  </div>

  <div className="admin-module-group">

    <button
      type="button"
      className={`
        admin-module-card
        ${
          openAdminSections.footerSettings
            ? "admin-module-card-open"
            : ""
        }
      `}
      onClick={() =>
        toggleAdminSection(
          "footerSettings"
        )
      }
      aria-expanded={
        openAdminSections.footerSettings
      }
      aria-controls="admin-section-footerSettings"
    >

      <span className="admin-module-card-eyebrow">
        Site Configuration
      </span>

      <strong className="admin-module-card-title">
        Footer Settings
      </strong>

      <span className="admin-module-card-description">
        Manage publication identity, footer links, contact information and social profiles.
      </span>

    </button>

    <section
      id="admin-section-footerSettings"
      className={`
        admin-module-panel
        ${
          openAdminSections.footerSettings
            ? "admin-module-panel-open"
            : "admin-module-panel-collapsed"
        }
      `}
      aria-hidden={
        !openAdminSections.footerSettings
      }
    >

      <div className="admin-module-panel-actions">

        <button
          type="button"
          className="
            admin-button
            admin-button-small
            admin-button-secondary
            admin-module-minimize
          "
          onClick={() =>
            minimizeAdminSection(
              "footerSettings"
            )
          }
          aria-label="Minimize Footer Settings"
        >
          Minimize
        </button>

      </div>

<div className="admin-section-heading admin-management-heading">

  <span className="admin-section-eyebrow">
    Site Configuration
  </span>

  <h2>
    Footer Settings
  </h2>

  <p>
    Manage publication identity, footer links,
    contact information and social profiles.
  </p>

</div>


<section className="admin-settings-section">

  {loadingFooter ? (

    <div className="admin-footer-state">
      Loading footer settings...
    </div>

  ) : (

    <>

      {/* ========================================= */}
      {/* PUBLICATION IDENTITY */}
      {/* ========================================= */}

      <div className="admin-settings-card">

        <div className="admin-footer-card-heading">

          <span className="admin-footer-card-eyebrow">
            Brand & Identity
          </span>

          <h3>
            Publication Identity
          </h3>

          <p>
            Control the publication name, tagline,
            footer description and footer logo.
          </p>

        </div>


        <div className="admin-footer-field-grid">

          <div className="admin-footer-field">

            <label>
              Organisation / Publication Name
            </label>

            <input
              type="text"
              value={footerData.publicationName}
              onChange={(e) =>
                updateFooterField(
                  "publicationName",
                  e.target.value
                )
              }
            />

          </div>


          <div className="admin-footer-field">

            <label>
              Tagline
            </label>

            <input
              type="text"
              value={footerData.tagline}
              onChange={(e) =>
                updateFooterField(
                  "tagline",
                  e.target.value
                )
              }
            />

          </div>

        </div>


        <div className="admin-footer-field">

          <label>
            Description
          </label>

          <div className="admin-footer-editor-wrap">

            <FooterDescriptionEditor
              value={footerData.description || ""}
              onChange={(content) =>
                updateFooterField(
                  "description",
                  content
                )
              }
            />

          </div>

        </div>


        <div className="admin-footer-field">

          <label>
            Footer Logo
          </label>

          <div className="admin-footer-logo-row">

            <input
              ref={footerLogoInputRef}
              type="file"
              accept="image/*"
              onChange={handleFooterLogoUpload}
              style={{ display: "none" }}
            />


            <button
              type="button"
              className="
                admin-button
                admin-button-small
                admin-button-secondary
              "
              onClick={() =>
                footerLogoInputRef.current?.click()
              }
            >
              Upload Image
            </button>


            {footerData.logo && (

              <div className="admin-footer-logo-selected">

                <div className="admin-footer-logo-preview">

                  <img
                    src={footerData.logo}
                    alt="Footer logo preview"
                  />

                </div>

                <span>
                  Logo selected
                </span>

              </div>

            )}

          </div>

        </div>

      </div>


      {/* ========================================= */}
      {/* QUICK LINKS */}
      {/* ========================================= */}

      <div className="admin-settings-card">

        <div className="admin-footer-card-heading">

          <span className="admin-footer-card-eyebrow">
            Navigation
          </span>

          <h3>
            Quick Links
          </h3>

          <p>
            Rename, show or hide, and reorder
            the top-level links displayed in the footer.
          </p>

        </div>


        <p className="admin-help-text">
          Only top-level categories are available here.
          Subcategories are intentionally excluded.
        </p>


        <div className="admin-footer-links-list">

          {[...footerData.quickLinks]
            .sort(
              (a, b) =>
                a.order - b.order
            )
            .map((link, index) => (

              <div
                key={link.path}
                className="admin-footer-link-row"
              >

                <div className="admin-footer-link-info">

                  <input
                    type="text"
                    value={link.label}
                    onChange={(e) =>
                      updateFooterLink(
                        index,
                        "label",
                        e.target.value
                      )
                    }
                  />

                  <span>
                    {link.path}
                  </span>

                </div>


                <label className="admin-footer-toggle">

                  <input
                    type="checkbox"
                    checked={link.enabled}
                    onChange={(e) =>
                      updateFooterLink(
                        index,
                        "enabled",
                        e.target.checked
                      )
                    }
                  />

                  <span>
                    Show
                  </span>

                </label>


                <div className="admin-footer-order-buttons">

                  <button
                    type="button"
                    className="
                      admin-button
                      admin-button-icon
                    "
                    onClick={() =>
                      moveFooterLink(
                        index,
                        -1
                      )
                    }
                    disabled={index === 0}
                    aria-label={`Move ${link.label} up`}
                  >
                    ↑
                  </button>


                  <button
                    type="button"
                    className="
                      admin-button
                      admin-button-icon
                    "
                    onClick={() =>
                      moveFooterLink(
                        index,
                        1
                      )
                    }
                    disabled={
                      index ===
                      footerData.quickLinks.length - 1
                    }
                    aria-label={`Move ${link.label} down`}
                  >
                    ↓
                  </button>

                </div>

              </div>

            ))}

        </div>

      </div>


      {/* ========================================= */}
      {/* CONTACT */}
      {/* ========================================= */}

      <div className="admin-settings-card">

        <div className="admin-footer-card-heading">

          <span className="admin-footer-card-eyebrow">
            Contact Details
          </span>

          <h3>
            Contact Information
          </h3>

          <p>
            Manage the address and public contact
            information displayed in the footer.
          </p>

        </div>


        <div className="admin-footer-field">

          <label>
            Address
          </label>

          <textarea
            rows="3"
            value={footerData.contact?.address || ""}
            onChange={(e) =>
              updateFooterContact(
                "address",
                e.target.value
              )
            }
          />

        </div>


        <div className="admin-footer-field-grid">

          <div className="admin-footer-field">

            <label>
              Email
            </label>

            <input
              type="email"
              value={footerData.contact?.email || ""}
              onChange={(e) =>
                updateFooterContact(
                  "email",
                  e.target.value
                )
              }
            />

          </div>


          <div className="admin-footer-field">

            <label>
              Phone
            </label>

            <input
              type="text"
              value={footerData.contact?.phone || ""}
              onChange={(e) =>
                updateFooterContact(
                  "phone",
                  e.target.value
                )
              }
            />

          </div>


          <div className="admin-footer-field admin-footer-field-full">

            <label>
              Website
            </label>

            <input
              type="url"
              value={footerData.contact?.website || ""}
              onChange={(e) =>
                updateFooterContact(
                  "website",
                  e.target.value
                )
              }
              placeholder="https://example.com"
            />

          </div>

        </div>

      </div>


      {/* ========================================= */}
      {/* SOCIAL MEDIA */}
      {/* ========================================= */}

      <div className="admin-settings-card">

        <div className="admin-footer-card-heading">

          <span className="admin-footer-card-eyebrow">
            Social Presence
          </span>

          <h3>
            Social Media
          </h3>

          <p>
            Add or update the public social-profile
            links shown in the site footer.
          </p>

        </div>


        <div className="admin-footer-field-grid">

          <div className="admin-footer-field">
            <label>Facebook</label>
            <input
              type="url"
              value={footerData.socialLinks?.facebook || ""}
              onChange={(e) =>
                updateFooterSocial(
                  "facebook",
                  e.target.value
                )
              }
            />
          </div>

          <div className="admin-footer-field">
            <label>Instagram</label>
            <input
              type="url"
              value={footerData.socialLinks?.instagram || ""}
              onChange={(e) =>
                updateFooterSocial(
                  "instagram",
                  e.target.value
                )
              }
            />
          </div>

          <div className="admin-footer-field">
            <label>Threads</label>
            <input
              type="url"
              value={footerData.socialLinks?.threads || ""}
              onChange={(e) =>
                updateFooterSocial(
                  "threads",
                  e.target.value
                )
              }
            />
          </div>

          <div className="admin-footer-field">
            <label>X</label>
            <input
              type="url"
              value={footerData.socialLinks?.x || ""}
              onChange={(e) =>
                updateFooterSocial(
                  "x",
                  e.target.value
                )
              }
            />
          </div>

          <div className="admin-footer-field">
            <label>YouTube</label>
            <input
              type="url"
              value={footerData.socialLinks?.youtube || ""}
              onChange={(e) =>
                updateFooterSocial(
                  "youtube",
                  e.target.value
                )
              }
            />
          </div>

          <div className="admin-footer-field">
            <label>LinkedIn</label>
            <input
              type="url"
              value={footerData.socialLinks?.linkedin || ""}
              onChange={(e) =>
                updateFooterSocial(
                  "linkedin",
                  e.target.value
                )
              }
            />
          </div>

          <div className="admin-footer-field admin-footer-field-full">
            <label>Telegram</label>
            <input
              type="url"
              value={footerData.socialLinks?.telegram || ""}
              onChange={(e) =>
                updateFooterSocial(
                  "telegram",
                  e.target.value
                )
              }
            />
          </div>

        </div>

      </div>


      {/* ========================================= */}
      {/* SAVE */}
      {/* ========================================= */}

      <div className="admin-footer-save-row">

        <button
          type="button"
          className="
            admin-button
            admin-button-primary
            admin-footer-save-button
          "
          onClick={handleFooterSave}
        >
          Save Footer Settings
        </button>

      </div>

    </>

  )}

</section>

    </section>

  </div>

  </div>

</section>


{showDeletePopup && (

<div className="delete-popup-overlay">

    <div className="delete-popup">

        <h2>

            Delete Confirmation

        </h2>

        <p>

            Are you sure you want to delete

            <strong>

                {" "}

                {deleteTarget.length}

                {" "}

                {deleteTarget.length === 1
                    ? "post"
                    : "posts"}

            </strong>

            ?

        </p>

        <p>

            This action cannot be undone.

        </p>

        <div className="delete-popup-buttons">

            <button
                onClick={() => {

                    setShowDeletePopup(false);

                    setDeleteTarget([]);

                }}
            >

                Cancel

            </button>

            <button
              onClick={confirmDelete}
          >

              Delete

          </button>

        </div>

    </div>

</div>

)}


{showCropModal && cropFile && (
  <ImageCropModal
    file={cropFile}
    aspect={cropAspect}
    title={
  cropTarget === "publication"
    ? `Crop ${
        publicationData.type === "book"
          ? "Book Cover"
          : "Magazine Cover"
      }`
    : isVideo
      ? "Crop Video Thumbnail"
      : "Crop Cover Image"
}
    onCancel={() => {

  setShowCropModal(false);
  setCropFile(null);

  if (cropTarget === "publication") {

    if (publicationCoverImageInputRef.current) {
      publicationCoverImageInputRef.current.value = "";
    }

  } else {

    if (coverImageInputRef.current) {
      coverImageInputRef.current.value = "";
    }

  }

  setCropTarget("post");
  setCropAspect(1);

}}
    onApply={(croppedFile) => {

  if (croppedFile.size > MAX_IMAGE_SIZE) {

    alert(
      `Cropped image size is ${(croppedFile.size / 1024 / 1024).toFixed(2)} MB. Maximum allowed size is 3 MB.`
    );

    return;
  }

  if (cropTarget === "publication") {

    setPublicationCoverImage(
      croppedFile
    );

  } else {

    setFormData((prev) => ({
      ...prev,
      coverImage: croppedFile
    }));

  }

  setShowCropModal(false);
  setCropFile(null);

  setCropTarget("post");
  setCropAspect(1);

}}
  />
)}

    </div>
  );


}

export default Admin;
